// game/gameState.js

import { SKILLS, BANK_CONFIG, FARM_CONFIG, BLOOMHOLD_CONFIG, COMBAT_CONFIG, HUSBANDRY_CONFIG } from './constants.js';
import { getItem } from '../data/items/index.js';
import { getTalentFlatBonus } from '../systems/talentTree/talentTree.js';
import { getQuest, getTotalQuestCount } from '../data/quests/questData.js';
import { getPetData } from '../skills/husbandry/husbandryData.js';

// Inventory constants
const INVENTORY_SLOTS = 28;
const MAX_STACK = Number.MAX_SAFE_INTEGER; // 9007199254740991

// Bank constants (also exported from BANK_CONFIG)
const BANK_MAX_SLOTS = BANK_CONFIG.MAX_SLOTS;
const BANK_MAX_STACK = BANK_CONFIG.MAX_STACK;

// Farm constants (also exported from FARM_CONFIG)
const FARM_MAX_PLOTS = FARM_CONFIG.MAX_PLOTS;

// BloomHold constants
const BLOOMHOLD_MAX_SLOTS = BLOOMHOLD_CONFIG.MAX_SLOTS;
const BLOOMHOLD_MAX_STACK = BLOOMHOLD_CONFIG.MAX_STACK;

// Loot Chest constants
const LOOT_CHEST_MAX_SLOTS = 10;
const LOOT_CHEST_BASE_STACK = 25;  // Base stack limit per slot

// Guild Chest constants (base values - scales with architecting)
const GUILD_CHEST_BASE_SLOTS = 8;   // Reduced from 15 (36 unique items possible)
const GUILD_CHEST_SLOTS_PER_10_LEVELS = 2;  // +2 per 10 arch levels
const GUILD_CHEST_MAX_SLOTS = 30;   // 24 from levels + 6 from Pilferer's Network
const GUILD_CHEST_BASE_STACK = 25;  // Base stack limit per slot

// Pet constants
const MAX_EVOLUTION = 2;

class GameState {
    constructor() {
        this.player = {
            skills: this.initializeSkills(),
            // Slot-based inventory: array of { itemId, quantity } or null for empty slots
            inventorySlots: new Array(INVENTORY_SLOTS).fill(null),
            // Slot-based bank: array of { itemId, quantity } - grows dynamically up to BANK_MAX_SLOTS
            bankSlots: [],
            // Farm plots: array of plot objects for passive crop/tree growth
            farmPlots: this.initializeFarmPlots(),
            // Farm settings for auto-harvest and auto-plant
            farmSettings: {
                autoHarvestEnabled: false,
                autoHarvestDestination: 'inventory', // 'inventory' | 'bank'
                autoPlantEnabled: true, // Toggle for Eternal Garden talent auto-replant
                autoPlantMode: 'last', // 'last' = replant same crop, 'specific' = replant chosen seed
                autoPlantCropType: null, // 'crop' | 'herb' | 'tree' (when mode is 'specific')
                autoPlantCropId: null, // e.g. 'POTATO', 'GREENLEAF' (when mode is 'specific')
            },
            // Purchased tools per skill - array of tool IDs that have been bought
            purchasedTools: {},
            // Equipped tool per skill - the tool ID currently selected
            equippedTools: {},
            // Locked items (won't be deposited with "Deposit All")
            // Stores item IDs, so lock follows the item when moved
            lockedItems: [],
            // BloomHold: specialized seed vault for farming items
            bloomholdSlots: [],
            bloomholdUnlocked: false,
            bloomholdAutoDeposit: false, // Auto-deposit eligible items to BloomHold on Deposit All
            // Loot Chest: Shared chest for pet rewards (50 slot limit)
            lootChest: [],
            // Pets: Array of pet states
            pets: [
                { id: 'rootling', unlocked: true, evolution: 0, foodFed: 0, contractId: null, contractStartedAt: null, secondaryContractId: null, secondaryContractStartedAt: null, lastSecondaryContractId: null, selectedContractType: null, patrolZone: null },
                { id: 'felltracker', unlocked: false, evolution: 0, foodFed: 0, contractId: null, contractStartedAt: null, secondaryContractId: null, secondaryContractStartedAt: null, lastSecondaryContractId: null, selectedContractType: null, patrolZone: null },
                { id: 'ichorebound', unlocked: false, evolution: 0, foodFed: 0, contractId: null, contractStartedAt: null, secondaryContractId: null, secondaryContractStartedAt: null, lastSecondaryContractId: null, selectedContractType: null, patrolZone: null },
                { id: 'chimera', unlocked: false, evolution: 0, foodFed: 0, contractId: null, contractStartedAt: null, secondaryContractId: null, secondaryContractStartedAt: null, lastSecondaryContractId: null, selectedContractType: null, patrolZone: null },
                { id: 'eidolon', unlocked: false, evolution: 0, foodFed: 0, contractId: null, contractStartedAt: null, secondaryContractId: null, secondaryContractStartedAt: null, lastSecondaryContractId: null, selectedContractType: null, patrolZone: null }
            ],
            // Completed contract types for pet unlocks
            completedContractTypes: [],
            // Player's active contracts (not pet contracts)
            activeContracts: [],
            // Current gatherer zone ID
            gathererZone: null,
            currentAction: null,
            stats: {
                totalLevel: 0,
                combatLevel: 1,
            },
            // Corruption buff state (per-skill)
            // New format: { skills: { [skillId]: { active, startedAt, remainingMs, extended } } }
            corruption: {
                skills: {}
            },
            // Tome of Echoes: tracks all items ever obtained
            // Format: { itemCounts: { [itemId]: totalQuantityObtained } }
            tomeOfEchoes: {
                itemCounts: {}
            },
            // Combat state
            combat: {
                currentHp: COMBAT_CONFIG.MIN_HP,   // Current HP (starts at 10 at level 1)
                inCombat: false,
                currentZone: null,
                currentTier: null,             // For tiered zones (e.g. 't1', 't2', 't3')
                currentMob: null,
                mobCurrentHp: 0,
                lastPlayerAttack: 0,
                lastMobAttack: 0,
                lastEatTime: 0,                    // For eat cooldown tracking
                killCounts: {},                    // { mobId: count } for bounty
                totalKills: 0,
                killHistory: [],                   // [{ timestamp, mobId }] for kills/hour calculation
                isDead: false,
                deathTime: null,                   // When death occurred (for respawn timer)
                // Auto-eat system
                autoEat: {
                    unlocked: false,
                    enabled: false,
                    storedFood: null,              // { itemId, quantity }
                    threshold: 50                  // HP % threshold (10-95)
                },
                // Apothecary Belt - auto-potion system (4 slots per equipment set)
                apothecaryBelt: {
                    unlocked: false,
                    enabled: false,
                    slots: {
                        melee: [null, null, null, null],
                        ranged: [null, null, null, null],
                        magic: [null, null, null, null]
                    }
                },
                // Zone progress for clearing and target farming
                // { [zoneId]: { cleared, killsAtClear, clearedAt, lifetimeKills } }
                zoneProgress: {},
                // Boss kill tracking for endgame progression gating
                // { [bossId]: killCount }
                bossKills: {},
                // Mob research from pet patrols { mobId: contractsCompleted }
                mobResearch: {},
                // Combat loot chest - items from kills go here before collecting
                // { items: { itemId: quantity }, ... }
                lootChest: {}
            },
            // Equipped combat gear (10 slots total)
            equippedGear: {
                weapon: null,    // Main hand
                offhand: null,   // Off hand (shields, secondary weapons)
                head: null,
                neck: null,      // Necklaces, amulets
                shoulders: null,
                hands: null,
                body: null,
                legs: null,
                ring: null,      // Single ring slot (may expand to 2 later)
                feet: null
            },
            // Equipment sets - each set stores its own gear independently
            // Swapping sets just switches which set is active (no items move to inventory)
            equipmentSets: {
                melee: {
                    name: 'Melee',
                    icon: '⚔️',
                    gear: { weapon: null, offhand: null, back: null, head: null, neck: null, shoulders: null, hands: null, body: null, legs: null, ring: null, feet: null, ammo: null },
                    ammoQuantity: 0
                },
                ranged: {
                    name: 'Ranged',
                    icon: '🏹',
                    gear: { weapon: null, offhand: null, back: null, head: null, neck: null, shoulders: null, hands: null, body: null, legs: null, ring: null, feet: null, ammo: null },
                    ammoQuantity: 0
                },
                magic: {
                    name: 'Magic',
                    icon: '✨',
                    gear: { weapon: null, offhand: null, back: null, head: null, neck: null, shoulders: null, hands: null, body: null, legs: null, ring: null, feet: null, ammo: null },
                    ammoQuantity: 0
                }
            },
            activeEquipmentSet: 'melee',  // 'melee', 'ranged', or 'magic'
            // Glyph Pouch: 6 slots for storing glyphs (allows quick spell switching)
            // Locked by default until player unlocks it (method TBD)
            glyphPouch: {
                unlocked: false,
                slots: [
                    { itemId: null, quantity: 0 },
                    { itemId: null, quantity: 0 },
                    { itemId: null, quantity: 0 },
                    { itemId: null, quantity: 0 },
                    { itemId: null, quantity: 0 },
                    { itemId: null, quantity: 0 }
                ]
            },
            // Pilfering Guild: Passive gathering system
            pilferingGuild: {
                unlocked: false,
                chest: [],  // Array of { itemId, quantity }
                pilferers: [
                    { domainId: 'mining', active: false, startedAt: null, lastItemId: null },
                    { domainId: 'smithing', active: false, startedAt: null, lastItemId: null },
                    { domainId: 'textiles', active: false, startedAt: null, lastItemId: null },
                    { domainId: 'hunting', active: false, startedAt: null, lastItemId: null },
                    { domainId: 'foods', active: false, startedAt: null, lastItemId: null },
                    { domainId: 'seeds', active: false, startedAt: null, lastItemId: null },
                    { domainId: 'glyphs', active: false, startedAt: null, lastItemId: null }
                ],
                unlockedPilfererCount: 1,
                totalItemsGathered: 0,
                contractAssistProcs: 0
            },
            // Architecting: Blueprints, Milestones and Civic Contracts
            architecting: {
                activeBlueprint: null,        // { blueprintId, startedAt, duration } - passive like husbandry
                completedMilestones: [],      // Array of milestone IDs
                activeMilestone: null,        // { milestoneId, startedAt, duration }
                activeCivicContract: null,    // { contractId, currentPhase, phaseStartedAt, status }
                completedCivicContracts: [],  // Array of contract IDs
                availableCivicContracts: []   // Array of contract IDs available to accept
            },
            // Pyre Veil: Hidden content revealed by Pyre level
            pyreVeil: {
                seenLore: [],           // Lore IDs already shown (e.g., ['pyre_15', 'pyre_35'])
                loreQueue: [],          // Lore IDs queued for display after offline modal
                discoveredMobs: []      // Hidden mob IDs revealed (for first-time animations)
            },
            // Talent Trees: Per-skill talent progression
            // Format: { [skillId]: { nodes: { [nodeId]: rank }, spentPoints: number } }
            talentTrees: {},
            // Quests: Progression system with requirements and rewards
            quests: {
                completed: [],           // Array of quest numbers completed
                active: null,            // { questNumber, startedAt } or null
                unlocks: [],             // Array of unlock strings (glyph_pouch, quiver_leather, etc.)
                pinned: []               // Array of quest numbers pinned for tracking (max 3)
            },
            // Daily Shop: Tracks limited daily stock items
            // Format: { [dailyId]: { remaining: number, resetTime: timestamp } }
            dailyShop: {}
        };

        this.settings = {
            soundEnabled: true,
            musicEnabled: false,
            autoSave: true,
            virtualLevelsEnabled: false,
            chestNotificationThreshold: 'any', // 'off', 'any', 'half', 'full'
            showStatComparison: true,  // Show green/red stat comparison on equipment
        };

        this.time = {
            totalTicks: 0,
            sessionStart: Date.now(),
            lastSave: Date.now(),
        };
    }

    initializeSkills() {
        const skills = {};
        for (const skillId of Object.values(SKILLS)) {
            skills[skillId] = {
                level: 1,
                virtualLevel: 1,
                xp: 0,
                currentXp: 0,
                nextLevelXp: 83,
                xpHistory: [],
            };
        }
        return skills;
    }

    initializeFarmPlots() {
        const plots = [];
        for (let i = 0; i < FARM_MAX_PLOTS; i++) {
            plots.push({
                id: i,
                unlocked: i < FARM_CONFIG.STARTING_PLOTS,
                state: 'empty', // 'empty' | 'growing' | 'ready'
                plantedAt: null,
                growthDuration: null,
                cropType: null, // 'crop' | 'tree'
                cropId: null,
                cropData: null,
            });
        }
        return plots;
    }

    getSkill(skillId) {
        return this.player.skills[skillId];
    }

    setAction(action) {
        // Auto-respawn if starting a non-combat action while dead
        // (prevents stuck death state when combat tick is replaced)
        if (this.getCombat().isDead && action?.type !== 'combat') {
            this.respawn();
        }
        this.player.currentAction = action;
    }

    clearAction() {
        this.player.currentAction = null;
    }

    getCurrentAction() {
        return this.player.currentAction;
    }

    recalculateTotalLevel() {
        let total = 0;
        for (const skill of Object.values(this.player.skills)) {
            total += Math.min(skill.level, 99);
        }
        this.player.stats.totalLevel = total;
    }

    // ==================== TOME OF ECHOES METHODS ====================

    // Callback for new discoveries (set by main.js to show notifications)
    onNewDiscovery = null;

    /**
     * Record an item as obtained in the Tome of Echoes
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount obtained (default 1)
     * @returns {{ isNewDiscovery: boolean, discoveryNumber: number }}
     */
    recordItemObtained(itemId, quantity = 1) {
        const wasDiscovered = this.player.tomeOfEchoes.itemCounts[itemId] > 0;

        if (!this.player.tomeOfEchoes.itemCounts[itemId]) {
            this.player.tomeOfEchoes.itemCounts[itemId] = 0;
        }
        this.player.tomeOfEchoes.itemCounts[itemId] += quantity;

        const isNewDiscovery = !wasDiscovered;
        const discoveryNumber = isNewDiscovery
            ? Object.keys(this.player.tomeOfEchoes.itemCounts).filter(id => this.player.tomeOfEchoes.itemCounts[id] > 0).length
            : 0;

        // Trigger notification callback if this is a new discovery
        if (isNewDiscovery && this.onNewDiscovery) {
            this.onNewDiscovery(itemId, discoveryNumber);
        }

        return { isNewDiscovery, discoveryNumber };
    }

    /**
     * Get Tome of Echoes data
     * @returns {{ itemCounts: Object<string, number> }}
     */
    getTomeOfEchoes() {
        return this.player.tomeOfEchoes;
    }

    /**
     * Get total count of a specific item ever obtained
     * @param {string} itemId - The item identifier
     * @returns {number} Total quantity ever obtained
     */
    getTomeItemCount(itemId) {
        return this.player.tomeOfEchoes.itemCounts[itemId] || 0;
    }

    /**
     * Check if an item has ever been discovered
     * @param {string} itemId - The item identifier
     * @returns {boolean}
     */
    isItemDiscovered(itemId) {
        return this.player.tomeOfEchoes.itemCounts[itemId] > 0;
    }

    // ==================== INVENTORY METHODS (Slot-based) ====================

    /**
     * Find the slot index for an item, or -1 if not found
     * @param {string} itemId - The item identifier
     * @returns {number} Slot index or -1
     */
    findInventorySlot(itemId) {
        return this.player.inventorySlots.findIndex(
            slot => slot && slot.itemId === itemId
        );
    }

    /**
     * Find the first empty slot index, or -1 if inventory is full
     * @returns {number} Slot index or -1
     */
    findEmptyInventorySlot() {
        return this.player.inventorySlots.findIndex(slot => slot === null);
    }

    /**
     * Add item to inventory with overflow protection
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount to add (default 1)
     * @returns {{ success: boolean, added: number, reason?: string }}
     */
    addItem(itemId, quantity = 1) {
        if (quantity <= 0) return { success: false, added: 0 };

        // Check if item already exists in a slot
        const existingSlotIndex = this.findInventorySlot(itemId);

        if (existingSlotIndex !== -1) {
            // Add to existing stack
            const slot = this.player.inventorySlots[existingSlotIndex];
            const spaceLeft = MAX_STACK - slot.quantity;
            const actualAdd = Math.min(quantity, spaceLeft);

            if (actualAdd <= 0) {
                return { success: false, added: 0, reason: 'Stack full' };
            }

            slot.quantity += actualAdd;
            this.recordItemObtained(itemId, actualAdd);

            if (actualAdd < quantity) {
                return { success: true, added: actualAdd, reason: 'Partial add - stack limit reached' };
            }
            return { success: true, added: actualAdd };
        }

        // Item doesn't exist - find empty slot
        const emptySlotIndex = this.findEmptyInventorySlot();
        if (emptySlotIndex === -1) {
            return { success: false, added: 0, reason: 'Inventory full' };
        }

        // Create new stack
        const actualAdd = Math.min(quantity, MAX_STACK);
        this.player.inventorySlots[emptySlotIndex] = {
            itemId,
            quantity: actualAdd
        };
        this.recordItemObtained(itemId, actualAdd);

        if (actualAdd < quantity) {
            return { success: true, added: actualAdd, reason: 'Partial add - stack limit reached' };
        }
        return { success: true, added: actualAdd };
    }

    /**
     * Remove item from inventory
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount to remove (default 1)
     * @returns {boolean} Whether removal was successful
     */
    removeItem(itemId, quantity = 1) {
        const slotIndex = this.findInventorySlot(itemId);
        if (slotIndex === -1) return false;

        const slot = this.player.inventorySlots[slotIndex];
        if (slot.quantity < quantity) return false;

        slot.quantity -= quantity;
        if (slot.quantity <= 0) {
            this.player.inventorySlots[slotIndex] = null;
        }
        return true;
    }

    /**
     * Get the number of unique items in inventory
     * @returns {number}
     */
    getUniqueItemCount() {
        return this.player.inventorySlots.filter(slot => slot !== null).length;
    }

    /**
     * Get inventory as array of 28 slots for UI rendering
     * @returns {Array<{ itemId: string, quantity: number } | null>}
     */
    getInventorySlots() {
        return this.player.inventorySlots;
    }

    /**
     * Get item count for a specific item
     * @param {string} itemId
     * @returns {number}
     */
    getItemCount(itemId) {
        const slotIndex = this.findInventorySlot(itemId);
        if (slotIndex === -1) return 0;
        return this.player.inventorySlots[slotIndex].quantity;
    }

    /**
     * Check if inventory has space for a new item type
     * @returns {boolean}
     */
    hasInventorySpace() {
        return this.findEmptyInventorySlot() !== -1;
    }

    /**
     * Swap two inventory slots
     * @param {number} fromIndex - Source slot index
     * @param {number} toIndex - Destination slot index
     * @returns {boolean} Whether swap was successful
     */
    swapInventorySlots(fromIndex, toIndex) {
        if (fromIndex < 0 || fromIndex >= INVENTORY_SLOTS) return false;
        if (toIndex < 0 || toIndex >= INVENTORY_SLOTS) return false;
        if (fromIndex === toIndex) return false;

        const temp = this.player.inventorySlots[fromIndex];
        this.player.inventorySlots[fromIndex] = this.player.inventorySlots[toIndex];
        this.player.inventorySlots[toIndex] = temp;
        return true;
    }

    /**
     * Move item from one slot to another (merge stacks if same item)
     * @param {number} fromIndex - Source slot index
     * @param {number} toIndex - Destination slot index
     * @returns {{ success: boolean, reason?: string }}
     */
    moveInventoryItem(fromIndex, toIndex) {
        if (fromIndex < 0 || fromIndex >= INVENTORY_SLOTS) {
            return { success: false, reason: 'Invalid source slot' };
        }
        if (toIndex < 0 || toIndex >= INVENTORY_SLOTS) {
            return { success: false, reason: 'Invalid destination slot' };
        }
        if (fromIndex === toIndex) {
            return { success: false, reason: 'Same slot' };
        }

        const fromSlot = this.player.inventorySlots[fromIndex];
        const toSlot = this.player.inventorySlots[toIndex];

        if (!fromSlot) {
            return { success: false, reason: 'Source slot is empty' };
        }

        // If destination is empty, just move
        if (!toSlot) {
            this.player.inventorySlots[toIndex] = fromSlot;
            this.player.inventorySlots[fromIndex] = null;
            return { success: true };
        }

        // If same item type, merge stacks
        if (fromSlot.itemId === toSlot.itemId) {
            const spaceInTarget = MAX_STACK - toSlot.quantity;
            const amountToMove = Math.min(fromSlot.quantity, spaceInTarget);

            if (amountToMove === 0) {
                // Target stack is full, swap instead
                return this.swapInventorySlots(fromIndex, toIndex)
                    ? { success: true }
                    : { success: false, reason: 'Swap failed' };
            }

            toSlot.quantity += amountToMove;
            fromSlot.quantity -= amountToMove;

            if (fromSlot.quantity === 0) {
                this.player.inventorySlots[fromIndex] = null;
            }

            return { success: true };
        }

        // Different items - swap
        return this.swapInventorySlots(fromIndex, toIndex)
            ? { success: true }
            : { success: false, reason: 'Swap failed' };
    }

    // ==================== LOCKED ITEMS METHODS ====================

    /**
     * Check if an item is locked (won't be deposited)
     * @param {string} itemId - Item identifier
     * @returns {boolean}
     */
    isItemLocked(itemId) {
        if (!itemId) return false;
        return this.player.lockedItems.includes(itemId);
    }

    /**
     * Toggle the lock state of an item
     * @param {string} itemId - Item identifier
     * @returns {boolean} New lock state (true = locked)
     */
    toggleItemLock(itemId) {
        if (!itemId) return false;

        const index = this.player.lockedItems.indexOf(itemId);
        if (index === -1) {
            // Not locked, lock it
            this.player.lockedItems.push(itemId);
            return true;
        } else {
            // Locked, unlock it
            this.player.lockedItems.splice(index, 1);
            return false;
        }
    }

    /**
     * Get all locked item IDs
     * @returns {string[]}
     */
    getLockedItems() {
        return [...this.player.lockedItems];
    }

    /**
     * Clear all locked items
     */
    clearLockedItems() {
        this.player.lockedItems = [];
    }

    // ==================== BANK METHODS (Slot-based) ====================

    /**
     * Find the slot index for an item in bank, or -1 if not found
     * @param {string} itemId - The item identifier
     * @returns {number} Slot index or -1
     */
    findBankSlot(itemId) {
        return this.player.bankSlots.findIndex(
            slot => slot && slot.itemId === itemId
        );
    }

    /**
     * Add item to bank with overflow protection
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount to add (default 1)
     * @returns {{ success: boolean, added: number, reason?: string }}
     */
    addBankItem(itemId, quantity = 1) {
        // Check if item already exists in a slot
        const existingSlotIndex = this.findBankSlot(itemId);

        if (existingSlotIndex !== -1) {
            // Add to existing stack
            const slot = this.player.bankSlots[existingSlotIndex];
            const spaceLeft = BANK_MAX_STACK - slot.quantity;
            const actualAdd = Math.min(quantity, spaceLeft);

            if (actualAdd <= 0) {
                return { success: false, added: 0, reason: 'Stack full' };
            }

            slot.quantity += actualAdd;
            this.recordItemObtained(itemId, actualAdd);

            if (actualAdd < quantity) {
                return { success: true, added: actualAdd, reason: 'Partial add - stack limit reached' };
            }
            return { success: true, added: actualAdd };
        }

        // Item doesn't exist - check if bank has space
        const bankMaxSlots = this.getBankMaxSlots();
        if (this.player.bankSlots.length >= bankMaxSlots) {
            return { success: false, added: 0, reason: 'Bank full' };
        }

        // Add new slot
        const actualAdd = Math.min(quantity, BANK_MAX_STACK);
        this.player.bankSlots.push({
            itemId,
            quantity: actualAdd
        });
        this.recordItemObtained(itemId, actualAdd);

        if (actualAdd < quantity) {
            return { success: true, added: actualAdd, reason: 'Partial add - stack limit reached' };
        }
        return { success: true, added: actualAdd };
    }

    /**
     * Remove item from bank
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount to remove (default 1)
     * @returns {boolean} Whether removal was successful
     */
    removeBankItem(itemId, quantity = 1) {
        const slotIndex = this.findBankSlot(itemId);
        if (slotIndex === -1) return false;

        const slot = this.player.bankSlots[slotIndex];
        if (slot.quantity < quantity) return false;

        slot.quantity -= quantity;
        if (slot.quantity === 0) {
            // Remove the slot entirely
            this.player.bankSlots.splice(slotIndex, 1);
        }
        return true;
    }

    /**
     * Get the number of unique items in bank
     * @returns {number}
     */
    getBankUniqueCount() {
        return this.player.bankSlots.length;
    }

    /**
     * Get bank item count for a specific item
     * @param {string} itemId
     * @returns {number}
     */
    getBankItemCount(itemId) {
        const slotIndex = this.findBankSlot(itemId);
        if (slotIndex === -1) return 0;
        return this.player.bankSlots[slotIndex].quantity;
    }

    /**
     * Check if bank has space for a new item type
     * @returns {boolean}
     */
    hasBankSpace() {
        return this.player.bankSlots.length < this.getBankMaxSlots();
    }

    /**
     * Get bank contents as array for UI rendering
     * @returns {Array<{ itemId: string, quantity: number }>}
     */
    getBankSlots() {
        return this.player.bankSlots;
    }

    /**
     * Swap two bank slots
     * @param {number} fromIndex - Source slot index
     * @param {number} toIndex - Destination slot index
     * @returns {boolean} Whether swap was successful
     */
    swapBankSlots(fromIndex, toIndex) {
        const bankLength = this.player.bankSlots.length;
        if (fromIndex < 0 || fromIndex >= bankLength) return false;
        if (toIndex < 0 || toIndex >= bankLength) return false;
        if (fromIndex === toIndex) return false;

        const temp = this.player.bankSlots[fromIndex];
        this.player.bankSlots[fromIndex] = this.player.bankSlots[toIndex];
        this.player.bankSlots[toIndex] = temp;
        return true;
    }

    /**
     * Move bank item from one position to another
     * @param {number} fromIndex - Source slot index
     * @param {number} toIndex - Destination slot index
     * @returns {{ success: boolean, reason?: string }}
     */
    moveBankItem(fromIndex, toIndex) {
        const bankLength = this.player.bankSlots.length;
        if (fromIndex < 0 || fromIndex >= bankLength) {
            return { success: false, reason: 'Invalid source slot' };
        }
        if (toIndex < 0 || toIndex >= bankLength) {
            return { success: false, reason: 'Invalid destination slot' };
        }
        if (fromIndex === toIndex) {
            return { success: false, reason: 'Same slot' };
        }

        // For bank, we swap (unlike inventory which can have empty slots)
        return this.swapBankSlots(fromIndex, toIndex)
            ? { success: true }
            : { success: false, reason: 'Swap failed' };
    }

    // ==================== FARM PLOT METHODS ====================

    /**
     * Get all farm plots
     * @returns {Array} Farm plots array
     */
    getFarmPlots() {
        return this.player.farmPlots;
    }

    /**
     * Get a specific farm plot
     * @param {number} plotIndex - Plot index
     * @returns {object|null} Plot object or null
     */
    getFarmPlot(plotIndex) {
        if (plotIndex < 0 || plotIndex >= FARM_MAX_PLOTS) return null;
        return this.player.farmPlots[plotIndex];
    }

    /**
     * Get farm settings
     * @returns {object} Farm settings
     */
    getFarmSettings() {
        return this.player.farmSettings;
    }

    /**
     * Update a farm plot
     * @param {number} plotIndex - Plot index
     * @param {object} updates - Properties to update
     */
    updateFarmPlot(plotIndex, updates) {
        if (plotIndex < 0 || plotIndex >= FARM_MAX_PLOTS) return;
        Object.assign(this.player.farmPlots[plotIndex], updates);
    }

    /**
     * Update farm settings
     * @param {object} updates - Settings to update
     */
    updateFarmSettings(updates) {
        Object.assign(this.player.farmSettings, updates);
    }

    // ==================== BLOOMHOLD METHODS ====================

    /**
     * Check if BloomHold has been purchased (requires Greenhouse milestone first)
     * @returns {boolean}
     */
    isBloomholdUnlocked() {
        return this.player.bloomholdUnlocked;
    }

    /**
     * Check if BloomHold can be purchased (milestone built)
     * @returns {boolean}
     */
    canPurchaseBloomhold() {
        return this.hasMilestone('greenhouse');
    }

    /**
     * Check if auto-deposit to BloomHold is enabled
     * @returns {boolean}
     */
    isBloomholdAutoDepositEnabled() {
        return this.player.bloomholdAutoDeposit;
    }

    /**
     * Set auto-deposit to BloomHold preference
     * @param {boolean} enabled
     */
    setBloomholdAutoDeposit(enabled) {
        this.player.bloomholdAutoDeposit = enabled;
    }

    /**
     * Purchase BloomHold (deducts gold)
     * Requires: Greenhouse milestone + Farming level + Gold
     * @param {number} farmingLevel - Current farming level
     * @returns {{ success: boolean, reason?: string }}
     */
    purchaseBloomhold(farmingLevel) {
        if (this.player.bloomholdUnlocked) {
            return { success: false, reason: 'Already unlocked' };
        }
        // Check milestone prerequisite
        if (!this.hasMilestone('greenhouse')) {
            return { success: false, reason: 'Build Greenhouse milestone first (Architecting Lv35)' };
        }
        if (farmingLevel < BLOOMHOLD_CONFIG.UNLOCK_LEVEL) {
            return { success: false, reason: `Requires Farming level ${BLOOMHOLD_CONFIG.UNLOCK_LEVEL}` };
        }
        const goldCount = this.getItemCount('coins');
        if (goldCount < BLOOMHOLD_CONFIG.UNLOCK_PRICE) {
            return { success: false, reason: `Need ${BLOOMHOLD_CONFIG.UNLOCK_PRICE} gold` };
        }

        this.removeItem('coins', BLOOMHOLD_CONFIG.UNLOCK_PRICE);
        this.player.bloomholdUnlocked = true;
        return { success: true };
    }

    /**
     * Find the slot index for an item in BloomHold, or -1 if not found
     * @param {string} itemId - The item identifier
     * @returns {number} Slot index or -1
     */
    findBloomholdSlot(itemId) {
        return this.player.bloomholdSlots.findIndex(
            slot => slot && slot.itemId === itemId
        );
    }

    /**
     * Add item to BloomHold with overflow protection
     * Note: Eligibility check should be done by caller (bloomhold.js system)
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount to add (default 1)
     * @returns {{ success: boolean, added: number, reason?: string }}
     */
    addBloomholdItem(itemId, quantity = 1) {
        if (!this.player.bloomholdUnlocked) {
            return { success: false, added: 0, reason: 'BloomHold not unlocked' };
        }

        // Check if item already exists in a slot
        const existingSlotIndex = this.findBloomholdSlot(itemId);

        if (existingSlotIndex !== -1) {
            // Add to existing stack
            const slot = this.player.bloomholdSlots[existingSlotIndex];
            const spaceLeft = BLOOMHOLD_MAX_STACK - slot.quantity;
            const actualAdd = Math.min(quantity, spaceLeft);

            if (actualAdd <= 0) {
                return { success: false, added: 0, reason: 'Stack full' };
            }

            slot.quantity += actualAdd;
            this.recordItemObtained(itemId, actualAdd);

            if (actualAdd < quantity) {
                return { success: true, added: actualAdd, reason: 'Partial add - stack limit reached' };
            }
            return { success: true, added: actualAdd };
        }

        // Item doesn't exist - check if BloomHold has space
        const bloomholdMaxSlots = this.getBloomholdMaxSlots();
        if (this.player.bloomholdSlots.length >= bloomholdMaxSlots) {
            return { success: false, added: 0, reason: 'BloomHold full' };
        }

        // Add new slot
        const actualAdd = Math.min(quantity, BLOOMHOLD_MAX_STACK);
        this.player.bloomholdSlots.push({
            itemId,
            quantity: actualAdd
        });
        this.recordItemObtained(itemId, actualAdd);

        if (actualAdd < quantity) {
            return { success: true, added: actualAdd, reason: 'Partial add - stack limit reached' };
        }
        return { success: true, added: actualAdd };
    }

    /**
     * Remove item from BloomHold
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount to remove (default 1)
     * @returns {boolean} Whether removal was successful
     */
    removeBloomholdItem(itemId, quantity = 1) {
        const slotIndex = this.findBloomholdSlot(itemId);
        if (slotIndex === -1) return false;

        const slot = this.player.bloomholdSlots[slotIndex];
        if (slot.quantity < quantity) return false;

        slot.quantity -= quantity;
        if (slot.quantity === 0) {
            // Remove the slot entirely
            this.player.bloomholdSlots.splice(slotIndex, 1);
        }
        return true;
    }

    /**
     * Get the number of unique items in BloomHold
     * @returns {number}
     */
    getBloomholdUniqueCount() {
        return this.player.bloomholdSlots.length;
    }

    /**
     * Get BloomHold item count for a specific item
     * @param {string} itemId
     * @returns {number}
     */
    getBloomholdItemCount(itemId) {
        const slotIndex = this.findBloomholdSlot(itemId);
        if (slotIndex === -1) return 0;
        return this.player.bloomholdSlots[slotIndex].quantity;
    }

    /**
     * Check if BloomHold has space for a new item type
     * @returns {boolean}
     */
    hasBloomholdSpace() {
        return this.player.bloomholdSlots.length < this.getBloomholdMaxSlots();
    }

    /**
     * Get BloomHold contents as array for UI rendering
     * @returns {Array<{ itemId: string, quantity: number }>}
     */
    getBloomholdSlots() {
        return this.player.bloomholdSlots;
    }

    /**
     * Swap two BloomHold slots
     * @param {number} fromIndex - Source slot index
     * @param {number} toIndex - Destination slot index
     * @returns {boolean} Whether swap was successful
     */
    swapBloomholdSlots(fromIndex, toIndex) {
        const length = this.player.bloomholdSlots.length;
        if (fromIndex < 0 || fromIndex >= length) return false;
        if (toIndex < 0 || toIndex >= length) return false;
        if (fromIndex === toIndex) return false;

        const temp = this.player.bloomholdSlots[fromIndex];
        this.player.bloomholdSlots[fromIndex] = this.player.bloomholdSlots[toIndex];
        this.player.bloomholdSlots[toIndex] = temp;
        return true;
    }

    /**
     * Move BloomHold item from one position to another
     * @param {number} fromIndex - Source slot index
     * @param {number} toIndex - Destination slot index
     * @returns {{ success: boolean, reason?: string }}
     */
    moveBloomholdItem(fromIndex, toIndex) {
        const length = this.player.bloomholdSlots.length;
        if (fromIndex < 0 || fromIndex >= length) {
            return { success: false, reason: 'Invalid source slot' };
        }
        if (toIndex < 0 || toIndex >= length) {
            return { success: false, reason: 'Invalid destination slot' };
        }
        if (fromIndex === toIndex) {
            return { success: false, reason: 'Same slot' };
        }

        return this.swapBloomholdSlots(fromIndex, toIndex)
            ? { success: true }
            : { success: false, reason: 'Swap failed' };
    }

    // ==================== LOOT CHEST METHODS ====================

    /**
     * Get loot chest contents
     * @returns {Array<{ itemId: string, quantity: number }>}
     */
    getLootChest() {
        return this.player.lootChest;
    }

    /**
     * Find the slot index for an item in loot chest, or -1 if not found
     * @param {string} itemId - The item identifier
     * @returns {number} Slot index or -1
     */
    findLootChestSlot(itemId) {
        return this.player.lootChest.findIndex(
            slot => slot && slot.itemId === itemId
        );
    }

    /**
     * Add item to loot chest with overflow protection
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount to add (default 1)
     * @returns {{ success: boolean, added: number, reason?: string }}
     */
    addLootChestItem(itemId, quantity = 1) {
        const maxStack = this.getLootChestMaxStack();

        // Check if item already exists in a slot
        const existingSlotIndex = this.findLootChestSlot(itemId);

        if (existingSlotIndex !== -1) {
            // Add to existing stack (allow overflow for existing items above limit)
            const slot = this.player.lootChest[existingSlotIndex];
            const spaceLeft = Math.max(0, maxStack - slot.quantity);
            const actualAdd = Math.min(quantity, spaceLeft);

            if (actualAdd <= 0) {
                return { success: false, added: 0, reason: 'Stack full' };
            }

            slot.quantity += actualAdd;
            this.recordItemObtained(itemId, actualAdd);

            if (actualAdd < quantity) {
                return { success: true, added: actualAdd, reason: 'Partial add - stack limit reached' };
            }
            return { success: true, added: actualAdd };
        }

        // Item doesn't exist - check if loot chest has space
        const lootChestMaxSlots = this.getLootChestMaxSlots();
        if (this.player.lootChest.length >= lootChestMaxSlots) {
            return { success: false, added: 0, reason: 'Loot chest full' };
        }

        // Add new slot
        const actualAdd = Math.min(quantity, maxStack);
        this.player.lootChest.push({
            itemId,
            quantity: actualAdd
        });
        this.recordItemObtained(itemId, actualAdd);

        if (actualAdd < quantity) {
            return { success: true, added: actualAdd, reason: 'Partial add - stack limit reached' };
        }
        return { success: true, added: actualAdd };
    }

    /**
     * Remove item from loot chest
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount to remove (default 1)
     * @returns {boolean} Whether removal was successful
     */
    removeLootChestItem(itemId, quantity = 1) {
        const slotIndex = this.findLootChestSlot(itemId);
        if (slotIndex === -1) return false;

        const slot = this.player.lootChest[slotIndex];
        if (slot.quantity < quantity) return false;

        slot.quantity -= quantity;
        if (slot.quantity === 0) {
            // Remove the slot entirely
            this.player.lootChest.splice(slotIndex, 1);
        }
        return true;
    }

    /**
     * Get loot chest item count for a specific item
     * @param {string} itemId
     * @returns {number}
     */
    getLootChestItemCount(itemId) {
        const slotIndex = this.findLootChestSlot(itemId);
        if (slotIndex === -1) return 0;
        return this.player.lootChest[slotIndex].quantity;
    }

    /**
     * Get the number of unique items in loot chest
     * @returns {number}
     */
    getLootChestUniqueCount() {
        return this.player.lootChest.length;
    }

    /**
     * Check if loot chest has space for a new item type
     * @returns {boolean}
     */
    hasLootChestSpace() {
        return this.player.lootChest.length < this.getLootChestMaxSlots();
    }

    /**
     * Clear all items from loot chest
     */
    clearLootChest() {
        this.player.lootChest = [];
    }

    // ==================== PILFERING GUILD METHODS ====================

    /**
     * Check if pilfering guild is unlocked (requires Guild Hall milestone + unlock purchase)
     * @returns {boolean}
     */
    isGuildUnlocked() {
        return this.player.pilferingGuild?.unlocked || false;
    }

    /**
     * Check if Guild Hall milestone is built (prerequisite for guild unlock)
     * @returns {boolean}
     */
    hasGuildHallMilestone() {
        return this.hasMilestone('guild_hall');
    }

    /**
     * Unlock the pilfering guild
     */
    unlockGuild() {
        if (!this.player.pilferingGuild) {
            this.player.pilferingGuild = this.initializeGuildState();
        }
        this.player.pilferingGuild.unlocked = true;
    }

    /**
     * Lock the pilfering guild (admin/testing)
     */
    lockGuild() {
        if (this.player.pilferingGuild) {
            this.player.pilferingGuild.unlocked = false;
        }
    }

    /**
     * Initialize default guild state (for new saves or missing data)
     * @returns {Object}
     */
    initializeGuildState() {
        return {
            unlocked: false,
            chest: [],
            pilferers: [
                { domainId: 'mining', active: false, startedAt: null, lastItemId: null },
                { domainId: 'smithing', active: false, startedAt: null, lastItemId: null },
                { domainId: 'textiles', active: false, startedAt: null, lastItemId: null },
                { domainId: 'hunting', active: false, startedAt: null, lastItemId: null },
                { domainId: 'foods', active: false, startedAt: null, lastItemId: null },
                { domainId: 'seeds', active: false, startedAt: null, lastItemId: null },
                { domainId: 'glyphs', active: false, startedAt: null, lastItemId: null }
            ],
            unlockedPilfererCount: 1,
            totalItemsGathered: 0,
            contractAssistProcs: 0
        };
    }

    /**
     * Get guild chest array
     * @returns {Array<{ itemId: string, quantity: number }>}
     */
    getGuildChest() {
        return this.player.pilferingGuild?.chest || [];
    }

    /**
     * Get maximum guild chest slots based on architecting level + milestones
     * Formula: 8 base + 2 per 10 architecting levels + milestone bonuses, max 30
     * @returns {number}
     */
    getMaxGuildChestSlots() {
        const archLevel = this.getSkill(SKILLS.ARCHITECTING)?.level || 1;
        const levelBonus = Math.floor(archLevel / 10) * GUILD_CHEST_SLOTS_PER_10_LEVELS;
        // Pilferer's Network milestones (+2 slots each)
        let milestoneBonus = 0;
        if (this.hasMilestone('pilferers_network_i')) milestoneBonus += 2;
        if (this.hasMilestone('pilferers_network_ii')) milestoneBonus += 2;
        if (this.hasMilestone('pilferers_network_iii')) milestoneBonus += 2;
        return Math.min(GUILD_CHEST_MAX_SLOTS, GUILD_CHEST_BASE_SLOTS + levelBonus + milestoneBonus);
    }

    /**
     * Find the slot index for an item in guild chest, or -1 if not found
     * @param {string} itemId - The item identifier
     * @returns {number} Slot index or -1
     */
    findGuildChestSlot(itemId) {
        const chest = this.getGuildChest();
        return chest.findIndex(slot => slot && slot.itemId === itemId);
    }

    /**
     * Add item to guild chest with overflow protection
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount to add (default 1)
     * @returns {{ success: boolean, added: number, reason?: string }}
     */
    addGuildChestItem(itemId, quantity = 1) {
        const chest = this.getGuildChest();
        const maxSlots = this.getMaxGuildChestSlots();
        const maxStack = this.getGuildChestMaxStack();

        // Check if item already exists in a slot
        const existingSlotIndex = this.findGuildChestSlot(itemId);

        if (existingSlotIndex !== -1) {
            // Add to existing stack (allow overflow for existing items above limit)
            const slot = chest[existingSlotIndex];
            const spaceLeft = Math.max(0, maxStack - slot.quantity);
            const actualAdd = Math.min(quantity, spaceLeft);

            if (actualAdd <= 0) {
                return { success: false, added: 0, reason: 'Stack full' };
            }

            slot.quantity += actualAdd;
            this.recordItemObtained(itemId, actualAdd);

            if (actualAdd < quantity) {
                return { success: true, added: actualAdd, reason: 'Partial add - stack limit reached' };
            }
            return { success: true, added: actualAdd };
        }

        // Item doesn't exist - check if guild chest has space
        // Architect's Sanctum grants infinite slots
        if (!this.isInfinitePilferingChest() && chest.length >= maxSlots) {
            return { success: false, added: 0, reason: 'Guild chest full' };
        }

        // Add new slot
        const actualAdd = Math.min(quantity, maxStack);
        chest.push({
            itemId,
            quantity: actualAdd
        });
        this.recordItemObtained(itemId, actualAdd);

        if (actualAdd < quantity) {
            return { success: true, added: actualAdd, reason: 'Partial add - stack limit reached' };
        }
        return { success: true, added: actualAdd };
    }

    /**
     * Remove item from guild chest
     * @param {string} itemId - The item identifier
     * @param {number} quantity - Amount to remove (default 1)
     * @returns {boolean} Whether removal was successful
     */
    removeGuildChestItem(itemId, quantity = 1) {
        const chest = this.getGuildChest();
        const slotIndex = this.findGuildChestSlot(itemId);
        if (slotIndex === -1) return false;

        const slot = chest[slotIndex];
        if (slot.quantity < quantity) return false;

        slot.quantity -= quantity;
        if (slot.quantity === 0) {
            // Remove the slot entirely
            chest.splice(slotIndex, 1);
        }
        return true;
    }

    /**
     * Get guild chest item count for a specific item
     * @param {string} itemId
     * @returns {number}
     */
    getGuildChestItemCount(itemId) {
        const slotIndex = this.findGuildChestSlot(itemId);
        if (slotIndex === -1) return 0;
        return this.getGuildChest()[slotIndex].quantity;
    }

    /**
     * Get the number of unique items in guild chest
     * @returns {number}
     */
    getGuildChestUniqueCount() {
        return this.getGuildChest().length;
    }

    /**
     * Check if guild chest has space for a new item type
     * @returns {boolean}
     */
    hasGuildChestSpace() {
        // Infinite chest from Architect's Sanctum milestone
        if (this.isInfinitePilferingChest()) return true;
        return this.getGuildChestUniqueCount() < this.getMaxGuildChestSlots();
    }

    /**
     * Check if guild chest is full (all slots used)
     * @returns {boolean}
     */
    isGuildChestFull() {
        // Never full with infinite chest from Architect's Sanctum
        if (this.isInfinitePilferingChest()) return false;
        return this.getGuildChestUniqueCount() >= this.getMaxGuildChestSlots();
    }

    /**
     * Check if a specific item can be added to the guild chest
     * @param {string} itemId - The item to check
     * @returns {boolean} Whether the item can be added (has room in stack or empty slot available)
     */
    canAddItemToChest(itemId) {
        // Infinite chest can always add
        if (this.isInfinitePilferingChest()) return true;

        const maxStack = this.getGuildChestMaxStack();
        const existingSlotIndex = this.findGuildChestSlot(itemId);

        if (existingSlotIndex !== -1) {
            // Item exists - check if stack has room
            const slot = this.getGuildChest()[existingSlotIndex];
            return slot.quantity < maxStack;
        }

        // Item doesn't exist - check if there's an empty slot
        return this.hasGuildChestSpace();
    }

    /**
     * Check if chest is effectively full for active pilferers
     * Returns true if NO active pilferer can add ANY of their domain's items
     * @param {Array} activeDomains - Array of domain objects with 'items' arrays
     * @returns {boolean}
     */
    isChestEffectivelyFull(activeDomains) {
        // Infinite chest is never effectively full
        if (this.isInfinitePilferingChest()) return false;

        // If no active domains, not effectively full (nothing to check)
        if (!activeDomains || activeDomains.length === 0) return false;

        // Check if ANY item from ANY active domain can be added
        for (const domain of activeDomains) {
            if (!domain.items) continue;
            for (const itemId of domain.items) {
                if (this.canAddItemToChest(itemId)) {
                    return false; // Found at least one item that can be added
                }
            }
        }

        // No items from any active domain can be added - effectively full
        return true;
    }

    /**
     * Clear all items from guild chest
     */
    clearGuildChest() {
        if (this.player.pilferingGuild) {
            this.player.pilferingGuild.chest = [];
        }
    }

    /**
     * Get all pilferers
     * @returns {Array}
     */
    getPilferers() {
        return this.player.pilferingGuild?.pilferers || [];
    }

    /**
     * Get a specific pilferer by domain ID
     * @param {string} domainId
     * @returns {Object|null}
     */
    getPilferer(domainId) {
        return this.getPilferers().find(p => p.domainId === domainId) || null;
    }

    /**
     * Get active pilferers
     * @returns {Array}
     */
    getActivePilferers() {
        return this.getPilferers().filter(p => p.active);
    }

    /**
     * Get number of unlocked pilferer slots
     * @returns {number}
     */
    getUnlockedPilfererCount() {
        return this.player.pilferingGuild?.unlockedPilfererCount || 1;
    }

    /**
     * Get number of currently active pilferers
     * @returns {number}
     */
    getActivePilfererCount() {
        return this.getActivePilferers().length;
    }

    /**
     * Check if can activate more pilferers
     * @returns {boolean}
     */
    canActivateMorePilferers() {
        return this.getActivePilfererCount() < this.getUnlockedPilfererCount();
    }

    /**
     * Start a pilferer on a domain
     * @param {string} domainId
     * @returns {{ success: boolean, reason?: string }}
     */
    startPilferer(domainId) {
        const pilferer = this.getPilferer(domainId);
        if (!pilferer) {
            return { success: false, reason: 'Invalid domain' };
        }

        if (pilferer.active) {
            return { success: false, reason: 'Pilferer already active' };
        }

        if (!this.canActivateMorePilferers()) {
            return { success: false, reason: 'No available pilferer slots' };
        }

        pilferer.active = true;
        pilferer.startedAt = Date.now();
        pilferer.lastItemId = null;

        return { success: true };
    }

    /**
     * Stop a pilferer
     * @param {string} domainId
     * @returns {{ success: boolean, reason?: string }}
     */
    stopPilferer(domainId) {
        const pilferer = this.getPilferer(domainId);
        if (!pilferer) {
            return { success: false, reason: 'Invalid domain' };
        }

        if (!pilferer.active) {
            return { success: false, reason: 'Pilferer not active' };
        }

        pilferer.active = false;
        pilferer.startedAt = null;

        return { success: true };
    }

    /**
     * Reset a pilferer's timer (after gathering completes)
     * @param {string} domainId
     * @param {string} lastItemId - The item that was gathered
     */
    resetPilfererTimer(domainId, lastItemId) {
        const pilferer = this.getPilferer(domainId);
        if (pilferer && pilferer.active) {
            pilferer.startedAt = Date.now();
            pilferer.lastItemId = lastItemId;
        }
    }

    /**
     * Increment total items gathered stat
     */
    incrementGuildItemsGathered() {
        if (this.player.pilferingGuild) {
            this.player.pilferingGuild.totalItemsGathered++;
        }
    }

    /**
     * Increment contract assist procs stat
     */
    incrementGuildContractAssists() {
        if (this.player.pilferingGuild) {
            this.player.pilferingGuild.contractAssistProcs++;
        }
    }

    /**
     * Get guild stats
     * @returns {{ totalItemsGathered: number, contractAssistProcs: number }}
     */
    getGuildStats() {
        return {
            totalItemsGathered: this.player.pilferingGuild?.totalItemsGathered || 0,
            contractAssistProcs: this.player.pilferingGuild?.contractAssistProcs || 0
        };
    }

    /**
     * Unlock additional pilferer slot
     * @returns {{ success: boolean, newCount: number }}
     */
    unlockPilfererSlot() {
        if (!this.player.pilferingGuild) return { success: false, newCount: 0 };

        const current = this.player.pilferingGuild.unlockedPilfererCount;
        if (current >= 7) {
            return { success: false, newCount: current };
        }

        this.player.pilferingGuild.unlockedPilfererCount = current + 1;
        return { success: true, newCount: current + 1 };
    }

    // ==================== ARCHITECTING MILESTONE METHODS ====================

    /**
     * Initialize architecting state if not exists
     */
    initializeArchitecting() {
        if (!this.player.architecting) {
            this.player.architecting = {
                activeBlueprints: [],
                completedMilestones: [],
                activeMilestone: null,
                activeCivicContract: null,
                completedCivicContracts: [],
                availableCivicContracts: [],
                autoQueueEnabled: true
            };
        }
        // Migrate old single blueprint to array
        if (this.player.architecting.activeBlueprint !== undefined) {
            if (this.player.architecting.activeBlueprint) {
                this.player.architecting.activeBlueprints = [this.player.architecting.activeBlueprint];
            } else {
                this.player.architecting.activeBlueprints = [];
            }
            delete this.player.architecting.activeBlueprint;
        }
        if (!this.player.architecting.activeBlueprints) {
            this.player.architecting.activeBlueprints = [];
        }
        if (!this.player.architecting.availableCivicContracts) {
            this.player.architecting.availableCivicContracts = [];
        }
        if (this.player.architecting.autoQueueEnabled === undefined) {
            this.player.architecting.autoQueueEnabled = true;
        }
        return this.player.architecting;
    }

    /**
     * Get first active blueprint (backwards compat)
     * @returns {Object|null} { blueprintId, startedAt, duration } or null
     */
    getActiveBlueprint() {
        const arch = this.initializeArchitecting();
        return arch.activeBlueprints[0] || null;
    }

    /**
     * Get all active blueprints
     * @returns {Array} Array of { blueprintId, startedAt, duration }
     */
    getActiveBlueprints() {
        const arch = this.initializeArchitecting();
        return arch.activeBlueprints;
    }

    /**
     * Start building a blueprint (passive)
     * @param {string} blueprintId - Blueprint identifier
     * @param {number} duration - Build duration in ms
     */
    startBlueprint(blueprintId, duration) {
        const arch = this.initializeArchitecting();
        arch.activeBlueprints.push({
            blueprintId,
            startedAt: Date.now(),
            duration
        });
    }

    /**
     * Stop/cancel a blueprint by index
     * @param {number} index - Blueprint slot index (0 or 1)
     */
    stopBlueprint(index = 0) {
        const arch = this.initializeArchitecting();
        if (index >= 0 && index < arch.activeBlueprints.length) {
            arch.activeBlueprints.splice(index, 1);
        }
    }

    /**
     * Get blueprint progress (0-1) for a specific slot
     * @param {number} index - Blueprint slot index (0 or 1)
     * @returns {number} Progress from 0 to 1
     */
    getBlueprintProgress(index = 0) {
        const arch = this.initializeArchitecting();
        const blueprint = arch.activeBlueprints[index];
        if (!blueprint) return 0;
        const elapsed = Date.now() - blueprint.startedAt;
        return Math.min(elapsed / blueprint.duration, 1);
    }

    /**
     * Get auto-queue enabled state
     */
    getAutoQueueEnabled() {
        const arch = this.initializeArchitecting();
        return arch.autoQueueEnabled;
    }

    /**
     * Set auto-queue enabled state
     */
    setAutoQueueEnabled(enabled) {
        const arch = this.initializeArchitecting();
        arch.autoQueueEnabled = enabled;
    }

    /**
     * Check if a milestone has been completed
     * @param {string} milestoneId - Milestone identifier
     * @returns {boolean}
     */
    hasMilestone(milestoneId) {
        const arch = this.initializeArchitecting();
        return arch.completedMilestones.includes(milestoneId);
    }

    /**
     * Mark a milestone as completed
     * @param {string} milestoneId - Milestone identifier
     */
    completeMilestone(milestoneId) {
        const arch = this.initializeArchitecting();
        if (!arch.completedMilestones.includes(milestoneId)) {
            arch.completedMilestones.push(milestoneId);
        }
    }

    /**
     * Get all completed milestones
     * @returns {Array} Array of milestone IDs
     */
    getCompletedMilestones() {
        const arch = this.initializeArchitecting();
        return arch.completedMilestones;
    }

    /**
     * Get active milestone being built
     * @returns {Object|null} Active milestone state or null
     */
    getActiveMilestone() {
        const arch = this.initializeArchitecting();
        return arch.activeMilestone;
    }

    /**
     * Set active milestone being built
     * @param {Object} milestone - { milestoneId, startedAt, duration }
     */
    setActiveMilestone(milestone) {
        const arch = this.initializeArchitecting();
        arch.activeMilestone = milestone;
    }

    /**
     * Clear active milestone
     */
    clearActiveMilestone() {
        const arch = this.initializeArchitecting();
        arch.activeMilestone = null;
    }

    // ==================== ARCHITECTING CIVIC CONTRACT METHODS ====================

    /**
     * Get active civic contract
     * @returns {Object|null} Active contract state or null
     */
    getActiveCivicContract() {
        const arch = this.initializeArchitecting();
        return arch.activeCivicContract;
    }

    /**
     * Set active civic contract
     * @param {Object} contract - { contractId, currentPhase, phaseStartedAt, status }
     */
    setActiveCivicContract(contract) {
        const arch = this.initializeArchitecting();
        arch.activeCivicContract = contract;
    }

    /**
     * Update active civic contract state
     * @param {Object} updates - Partial updates to merge
     */
    updateActiveCivicContract(updates) {
        const arch = this.initializeArchitecting();
        if (arch.activeCivicContract) {
            Object.assign(arch.activeCivicContract, updates);
        }
    }

    /**
     * Clear active civic contract
     */
    clearActiveCivicContract() {
        const arch = this.initializeArchitecting();
        arch.activeCivicContract = null;
    }

    /**
     * Mark a civic contract as completed
     * @param {string} contractId - Contract identifier
     */
    completeCivicContract(contractId) {
        const arch = this.initializeArchitecting();
        if (!arch.completedCivicContracts.includes(contractId)) {
            arch.completedCivicContracts.push(contractId);
        }
    }

    /**
     * Get all completed civic contracts
     * @returns {Array} Array of contract IDs
     */
    getCompletedCivicContracts() {
        const arch = this.initializeArchitecting();
        return arch.completedCivicContracts;
    }

    /**
     * Get available civic contracts that can be accepted
     * @returns {Array} Array of contract IDs
     */
    getAvailableCivicContracts() {
        const arch = this.initializeArchitecting();
        return arch.availableCivicContracts;
    }

    /**
     * Add a civic contract to available pool
     * @param {string} contractId - Contract identifier
     */
    addAvailableCivicContract(contractId) {
        const arch = this.initializeArchitecting();
        if (!arch.availableCivicContracts.includes(contractId)) {
            arch.availableCivicContracts.push(contractId);
        }
    }

    /**
     * Remove a civic contract from available pool (when accepted or expired)
     * @param {string} contractId - Contract identifier
     */
    removeAvailableCivicContract(contractId) {
        const arch = this.initializeArchitecting();
        const index = arch.availableCivicContracts.indexOf(contractId);
        if (index !== -1) {
            arch.availableCivicContracts.splice(index, 1);
        }
    }

    /**
     * Check if a civic contract is available
     * @param {string} contractId - Contract identifier
     * @returns {boolean}
     */
    hasCivicContractAvailable(contractId) {
        const arch = this.initializeArchitecting();
        return arch.availableCivicContracts.includes(contractId);
    }

    // ==================== ARCHITECTING STORAGE BONUS METHODS ====================

    /**
     * Get bank storage bonus from milestones
     * @returns {number} Bonus slots from architecting milestones
     */
    getBankStorageBonus() {
        let bonus = 0;
        if (this.hasMilestone('reinforced_storage_i')) bonus += 50;
        if (this.hasMilestone('reinforced_storage_ii')) bonus += 100;
        if (this.hasMilestone('reinforced_storage_iii')) bonus += 150;
        return bonus;
    }

    /**
     * Get pet chest storage bonus from milestones
     * @returns {number} Bonus slots from architecting milestones
     */
    getPetChestStorageBonus() {
        let bonus = 0;
        // Pet Quarters milestones (+4 slots each)
        if (this.hasMilestone('pet_quarters_i')) bonus += 4;
        if (this.hasMilestone('pet_quarters_ii')) bonus += 4;
        if (this.hasMilestone('pet_quarters_iii')) bonus += 4;
        // Beastmaster's Lodge milestones (+2 slots each)
        if (this.hasMilestone('beastmasters_lodge_i')) bonus += 2;
        if (this.hasMilestone('beastmasters_lodge_ii')) bonus += 2;
        if (this.hasMilestone('beastmasters_lodge_iii')) bonus += 2;
        return bonus;
    }

    /**
     * Get BloomHold storage bonus from milestones
     * @returns {number} Bonus slots from architecting milestones
     */
    getBloomholdStorageBonus() {
        let bonus = 0;
        if (this.hasMilestone('bloomhold_expansion')) bonus += 50;
        return bonus;
    }

    /**
     * Check if pilfering chest is infinite (legacy - no longer used)
     * Architect's Sanctum now enables auto-loot instead of infinite storage
     * @returns {boolean} Always false - infinite storage replaced by auto-loot
     * @deprecated Use hasAutoLootChests() instead
     */
    isInfinitePilferingChest() {
        return false; // Infinite storage replaced by auto-loot system
    }

    /**
     * Get pet chest stack bonus from milestones
     * @returns {number} Bonus stack size from architecting milestones
     */
    getPetChestStackBonus() {
        let bonus = 0;
        if (this.hasMilestone('pet_storage_i')) bonus += 25;
        if (this.hasMilestone('pet_storage_ii')) bonus += 50;
        if (this.hasMilestone('pet_storage_iii')) bonus += 100;
        return bonus;
    }

    /**
     * Get guild chest stack bonus from milestones
     * @returns {number} Bonus stack size from architecting milestones
     */
    getGuildChestStackBonus() {
        let bonus = 0;
        if (this.hasMilestone('guild_storage_i')) bonus += 25;
        if (this.hasMilestone('guild_storage_ii')) bonus += 50;
        if (this.hasMilestone('guild_storage_iii')) bonus += 100;
        return bonus;
    }

    /**
     * Get pilferer speed multiplier from Guild Vault milestones
     * Each tier grants +2% speed (multiplicative)
     * @returns {number} Speed multiplier (1.0 to ~1.061)
     */
    getPilfererSpeedMultiplier() {
        let multiplier = 1.0;
        if (this.hasMilestone('guild_storage_i')) multiplier *= 1.02;
        if (this.hasMilestone('guild_storage_ii')) multiplier *= 1.02;
        if (this.hasMilestone('guild_storage_iii')) multiplier *= 1.02;
        return multiplier;
    }

    /**
     * Get pet contract speed multiplier from Pet Pantry milestones
     * Each tier grants +2% speed (multiplicative)
     * @returns {number} Speed multiplier (1.0 to ~1.061)
     */
    getPetContractSpeedMultiplier() {
        let multiplier = 1.0;
        if (this.hasMilestone('pet_storage_i')) multiplier *= 1.02;
        if (this.hasMilestone('pet_storage_ii')) multiplier *= 1.02;
        if (this.hasMilestone('pet_storage_iii')) multiplier *= 1.02;
        return multiplier;
    }

    /**
     * Check if auto-loot chests is enabled (Architect's Sanctum)
     * @returns {boolean}
     */
    hasAutoLootChests() {
        return this.hasMilestone('architects_sanctum');
    }

    /**
     * Auto-loot item to bank (seeds to BloomHold first)
     * Used when Architect's Sanctum is unlocked
     * @param {string} itemId - Item to deposit
     * @param {number} quantity - Amount to deposit
     * @param {string} source - Source of loot ('pilfering' or 'husbandry')
     * @returns {object} { success, added, destination, reason? }
     */
    autoLootToBank(itemId, quantity, source = 'pilfering') {
        const item = getItem(itemId);
        if (!item) return { success: false, added: 0, reason: 'Invalid item' };

        // Check if BloomHold eligible (seeds and crops)
        const bloomholdCategories = BLOOMHOLD_CONFIG.ACCEPTED_CATEGORIES || ['seed', 'tree_seed', 'herb_seed', 'crop'];
        const isBloomholdItem = item.category && bloomholdCategories.includes(item.category);

        let totalAdded = 0;
        let destination = 'bank';

        // Try BloomHold first for eligible items if greenhouse unlocked
        if (isBloomholdItem && this.hasMilestone('greenhouse')) {
            const bloomholdResult = this.addBloomholdItem(itemId, quantity);
            if (bloomholdResult.success) {
                totalAdded += bloomholdResult.added || quantity;
                destination = 'bloomhold';

                // If all added, return success
                if (totalAdded >= quantity) {
                    return { success: true, added: totalAdded, destination, source };
                }

                // Otherwise, try bank for remainder
                quantity -= totalAdded;
            }
        }

        // Try bank for remaining quantity (or all if not BloomHold eligible)
        if (quantity > 0) {
            const bankResult = this.addBankItem(itemId, quantity);
            if (bankResult.success) {
                totalAdded += bankResult.added || quantity;
                destination = totalAdded > (bankResult.added || 0) ? 'both' : 'bank';
            } else if (totalAdded === 0) {
                // Both BloomHold and bank failed
                return { success: false, added: 0, reason: 'Storage full' };
            }
        }

        return { success: totalAdded > 0, added: totalAdded, destination, source };
    }

    // ==================== DYNAMIC STORAGE SLOT CALCULATIONS ====================

    /**
     * Get maximum bank slots (base + architecting bonuses)
     * @returns {number} Max slots available (200 base, 500 max)
     */
    getBankMaxSlots() {
        const baseSlots = BANK_CONFIG.BASE_SLOTS || 200;
        const bonus = this.getBankStorageBonus();
        return Math.min(baseSlots + bonus, BANK_CONFIG.MAX_SLOTS);
    }

    /**
     * Get maximum BloomHold slots (base + architecting bonuses)
     * @returns {number} Max slots available (50 base, 100 max)
     */
    getBloomholdMaxSlots() {
        const baseSlots = BLOOMHOLD_CONFIG.BASE_SLOTS || 50;
        const bonus = this.getBloomholdStorageBonus();
        return Math.min(baseSlots + bonus, BLOOMHOLD_CONFIG.MAX_SLOTS);
    }

    /**
     * Get maximum pet loot chest slots (base + architecting bonuses)
     * @returns {number} Max slots available (6 base, 18 max)
     */
    getLootChestMaxSlots() {
        const baseSlots = HUSBANDRY_CONFIG.CHEST_BASE_SLOTS;  // 6 base
        const bonus = this.getPetChestStorageBonus();
        return Math.min(baseSlots + bonus, HUSBANDRY_CONFIG.CHEST_MAX_SLOTS);  // 18 max
    }

    /**
     * Get maximum stack size per slot for pet loot chest
     * @returns {number} Max items per slot (25 base, up to 200)
     */
    getLootChestMaxStack() {
        return LOOT_CHEST_BASE_STACK + this.getPetChestStackBonus();
    }

    /**
     * Get maximum stack size per slot for guild chest
     * @returns {number} Max items per slot (25 base, up to 200)
     */
    getGuildChestMaxStack() {
        return GUILD_CHEST_BASE_STACK + this.getGuildChestStackBonus();
    }

    // ==================== PET METHODS ====================

    /**
     * Get all pets
     * @returns {Array} Pets array
     */
    getPets() {
        return this.player.pets;
    }

    /**
     * Get a single pet by ID
     * @param {string} petId - Pet identifier
     * @returns {object|null} Pet object or null
     */
    getPet(petId) {
        return this.player.pets.find(pet => pet.id === petId) || null;
    }

    /**
     * Unlock a pet
     * @param {string} petId - Pet identifier
     * @returns {boolean} Whether unlock was successful
     */
    unlockPet(petId) {
        const pet = this.getPet(petId);
        if (!pet) return false;
        pet.unlocked = true;
        return true;
    }

    /**
     * Evolve a pet (max evolution is 2)
     * @param {string} petId - Pet identifier
     * @returns {boolean} Whether evolution was successful
     */
    evolvePet(petId) {
        const pet = this.getPet(petId);
        if (!pet) return false;
        if (pet.evolution >= MAX_EVOLUTION) return false;
        pet.evolution++;
        // Reset food fed since new evolution tier requires different food type
        pet.foodFed = 0;
        return true;
    }

    /**
     * Feed a pet (add food to foodFed total)
     * @param {string} petId - Pet identifier
     * @param {number} amount - Amount of food to add (default 1)
     * @returns {boolean} Whether feeding was successful
     */
    feedPet(petId, amount = 1) {
        const pet = this.getPet(petId);
        if (!pet) return false;
        pet.foodFed = (pet.foodFed || 0) + amount;
        return true;
    }

    /**
     * Add overflow food to a pet (for Feeding Overflow talent)
     * @param {string} petId - Pet identifier
     * @param {number} amount - Amount of overflow food to add
     * @returns {boolean} Whether overflow was added
     */
    addPetFoodOverflow(petId, amount) {
        const pet = this.getPet(petId);
        if (!pet) return false;
        pet.foodOverflow = (pet.foodOverflow || 0) + amount;
        // Cap at 1000 (max +10% bonus)
        pet.foodOverflow = Math.min(pet.foodOverflow, 1000);
        return true;
    }

    /**
     * Boost a pet's contract progress by moving start time back (for Pack Instinct talent)
     * @param {string} petId - Pet identifier
     * @param {number} progressMs - Progress to add in milliseconds
     * @param {string} slot - 'primary' or 'secondary'
     * @returns {boolean} Whether progress was boosted
     */
    boostPetContractProgress(petId, progressMs, slot = 'primary') {
        const pet = this.getPet(petId);
        if (!pet) return false;

        if (slot === 'secondary') {
            if (!pet.secondaryContractStartedAt) return false;
            pet.secondaryContractStartedAt -= progressMs;
        } else {
            if (!pet.contractStartedAt) return false;
            pet.contractStartedAt -= progressMs;
        }
        return true;
    }

    /**
     * Check if a pet can be unlocked based on level and contract requirements
     * @param {string} petId - Pet identifier
     * @param {number} husbandryLevel - Current husbandry level
     * @returns {{ canUnlock: boolean, reason?: string }}
     */
    canUnlockPet(petId, husbandryLevel) {
        const pet = this.getPet(petId);
        if (!pet) return { canUnlock: false, reason: 'Pet not found' };
        if (pet.unlocked) return { canUnlock: false, reason: 'Already unlocked' };

        // Get pet data to check for quest requirement
        const petData = getPetData(petId);

        // Define pet unlock requirements
        const requirements = {
            rootling: { level: 1, contractTypes: [] },
            felltracker: { level: 15, contractTypes: ['resource'] },
            ichorebound: { level: 30, contractTypes: ['combat'] },
            chimera: { level: 50, contractTypes: ['resource', 'combat'] },
            eidolon: { level: 75, contractTypes: ['purification'] }
        };

        const req = requirements[petId];
        if (!req) return { canUnlock: false, reason: 'Unknown pet' };

        // Check level requirement
        if (husbandryLevel < req.level) {
            return { canUnlock: false, reason: `Requires Husbandry level ${req.level}` };
        }

        // Check quest unlock requirement
        if (petData?.questUnlock && !this.hasQuestUnlock(petData.questUnlock)) {
            const unlockName = this.getUnlockDisplayName(petData.questUnlock);
            return { canUnlock: false, reason: `Requires quest unlock: ${unlockName}` };
        }

        // Check contract type requirements
        for (const contractType of req.contractTypes) {
            if (!this.hasCompletedContractType(contractType)) {
                return { canUnlock: false, reason: `Must complete a ${contractType} contract first` };
            }
        }

        return { canUnlock: true };
    }

    // ==================== CONTRACT TYPE TRACKING ====================

    /**
     * Check if a contract type has been completed
     * @param {string} type - Contract type (resource, combat, purification)
     * @returns {boolean}
     */
    hasCompletedContractType(type) {
        return this.player.completedContractTypes.includes(type);
    }

    /**
     * Mark a contract type as completed
     * @param {string} type - Contract type to mark completed
     */
    markContractTypeCompleted(type) {
        if (!this.player.completedContractTypes.includes(type)) {
            this.player.completedContractTypes.push(type);
        }
    }

    // ==================== PET CONTRACT METHODS ====================

    /**
     * Start a contract for a pet
     * @param {string} petId - Pet identifier
     * @param {string} contractId - Contract identifier
     * @param {number} headStartMs - Optional head start in ms (for Rapid Deployment talent)
     * @returns {boolean} Whether contract was started
     */
    startPetContract(petId, contractId, headStartMs = 0) {
        const pet = this.getPet(petId);
        if (!pet) return false;
        if (pet.contractId) return false; // Already on a contract

        pet.contractId = contractId;
        // If head start provided, set start time in the past
        pet.contractStartedAt = Date.now() - headStartMs;
        return true;
    }

    /**
     * Stop/clear a pet's current contract
     * @param {string} petId - Pet identifier
     * @returns {boolean} Whether contract was stopped
     */
    stopPetContract(petId) {
        const pet = this.getPet(petId);
        if (!pet) return false;

        pet.contractId = null;
        pet.contractStartedAt = null;
        return true;
    }

    /**
     * Start a secondary contract for a pet (Eternal Bond talent)
     * @param {string} petId - Pet identifier
     * @param {string} contractId - Contract identifier
     * @param {number} headStartMs - Optional head start in ms
     * @returns {boolean} Whether secondary contract was started
     */
    startSecondaryPetContract(petId, contractId, headStartMs = 0) {
        const pet = this.getPet(petId);
        if (!pet) return false;
        if (pet.secondaryContractId) return false; // Already has secondary

        pet.secondaryContractId = contractId;
        pet.secondaryContractStartedAt = Date.now() - headStartMs;
        return true;
    }

    /**
     * Stop/clear a pet's secondary contract
     * @param {string} petId - Pet identifier
     * @returns {boolean} Whether secondary contract was stopped
     */
    stopSecondaryPetContract(petId) {
        const pet = this.getPet(petId);
        if (!pet) return false;

        pet.secondaryContractId = null;
        pet.secondaryContractStartedAt = null;
        return true;
    }

    /**
     * Set the last contract a pet was doing (for resuming after chest empties)
     * @param {string} petId - Pet identifier
     * @param {string} contractId - Contract identifier
     */
    setPetLastContract(petId, contractId) {
        const pet = this.getPet(petId);
        if (!pet) return;
        pet.lastContractId = contractId;
    }

    /**
     * Clear the last contract for a pet (after successfully restarting)
     * @param {string} petId - Pet identifier
     */
    clearPetLastContract(petId) {
        const pet = this.getPet(petId);
        if (!pet) return;
        pet.lastContractId = null;
    }

    /**
     * Set the last secondary contract for a pet (for resuming after chest empties)
     * @param {string} petId - Pet identifier
     * @param {string} contractId - Contract identifier
     */
    setPetLastSecondaryContract(petId, contractId) {
        const pet = this.getPet(petId);
        if (!pet) return;
        pet.lastSecondaryContractId = contractId;
    }

    /**
     * Clear the last secondary contract for a pet
     * @param {string} petId - Pet identifier
     */
    clearPetLastSecondaryContract(petId) {
        const pet = this.getPet(petId);
        if (!pet) return;
        pet.lastSecondaryContractId = null;
    }

    /**
     * Get all pets that are idle but have a last contract to resume
     * Also includes pets with only primary active but a pending secondary last contract
     * @returns {Array} Array of { petId, lastContractId, slot }
     */
    getIdlePetsWithLastContract() {
        const results = [];
        for (const pet of this.player.pets) {
            if (!pet.unlocked) continue;
            // Primary slot idle with last contract
            if (!pet.contractId && pet.lastContractId) {
                results.push({ petId: pet.id, lastContractId: pet.lastContractId, slot: 'primary' });
            }
            // Secondary slot idle with last secondary contract
            if (!pet.secondaryContractId && pet.lastSecondaryContractId) {
                results.push({ petId: pet.id, lastContractId: pet.lastSecondaryContractId, slot: 'secondary' });
            }
        }
        return results;
    }

    /**
     * Get the progress of a pet's contract (0-1)
     * @param {string} petId - Pet identifier
     * @param {number} contractDuration - Duration of contract in ms
     * @returns {number} Progress from 0 to 1
     */
    getPetContractProgress(petId, contractDuration) {
        const pet = this.getPet(petId);
        if (!pet || !pet.contractStartedAt || !pet.contractId) return 0;

        const elapsed = Date.now() - pet.contractStartedAt;
        return Math.min(elapsed / contractDuration, 1);
    }

    // ==================== PET PATROL & MOB RESEARCH METHODS ====================

    /**
     * Set a pet's patrol zone
     * @param {string} petId - Pet identifier
     * @param {string|null} zoneId - Zone to patrol, or null to clear
     * @returns {boolean} Whether update was successful
     */
    setPetPatrolZone(petId, zoneId) {
        const pet = this.getPet(petId);
        if (!pet) return false;
        pet.patrolZone = zoneId;
        return true;
    }

    /**
     * Get which zone a pet is patrolling
     * @param {string} petId - Pet identifier
     * @returns {string|null} Zone ID or null
     */
    getPetPatrolZone(petId) {
        const pet = this.getPet(petId);
        return pet ? pet.patrolZone : null;
    }

    /**
     * Get which combat-capable pet is patrolling a specific zone
     * @param {string} zoneId - Zone identifier
     * @returns {object|null} Pet object or null
     */
    getPatrollingPet(zoneId) {
        return this.player.pets.find(pet =>
            pet.unlocked && pet.patrolZone === zoneId
        ) || null;
    }

    /**
     * Get all combat-capable pets that are unlocked
     * @returns {Array} Array of combat-capable pets
     */
    getCombatCapablePets() {
        const combatPetIds = ['felltracker', 'chimera', 'eidolon'];
        return this.player.pets.filter(pet =>
            pet.unlocked && combatPetIds.includes(pet.id)
        );
    }

    /**
     * Get mob research progress (contracts completed)
     * @param {string} mobId - Mob identifier
     * @returns {number} Number of contracts completed
     */
    getMobResearch(mobId) {
        const combat = this.getCombat();
        if (!combat.mobResearch) combat.mobResearch = {};
        return combat.mobResearch[mobId] || 0;
    }

    /**
     * Add research progress for a mob
     * @param {string} mobId - Mob identifier
     * @param {number} amount - Amount to add (default 1)
     * @returns {number} New research level
     */
    addMobResearch(mobId, amount = 1) {
        const combat = this.getCombat();
        if (!combat.mobResearch) combat.mobResearch = {};
        combat.mobResearch[mobId] = (combat.mobResearch[mobId] || 0) + amount;
        return combat.mobResearch[mobId];
    }

    /**
     * Check if a mob's weakness is revealed (requires 3+ contracts)
     * @param {string} mobId - Mob identifier
     * @returns {boolean}
     */
    isMobWeaknessRevealed(mobId) {
        return this.getMobResearch(mobId) >= 3;
    }

    /**
     * Check if a mob's resistance is revealed (requires 6+ contracts)
     * @param {string} mobId - Mob identifier
     * @returns {boolean}
     */
    isMobResistanceRevealed(mobId) {
        return this.getMobResearch(mobId) >= 6;
    }

    /**
     * Get research level for a mob (0 = none, 1 = weakness, 2 = both)
     * @param {string} mobId - Mob identifier
     * @returns {number} Research level 0-2
     */
    getMobResearchLevel(mobId) {
        const contracts = this.getMobResearch(mobId);
        if (contracts >= 6) return 2;
        if (contracts >= 3) return 1;
        return 0;
    }

    // ==================== COMBAT LOOT CHEST METHODS ====================

    /**
     * Get combat loot chest contents
     * @returns {object} Object with itemId: quantity
     */
    getCombatLootChest() {
        const combat = this.getCombat();
        if (!combat.lootChest) combat.lootChest = {};
        return combat.lootChest;
    }

    /**
     * Get count of unique items in combat loot chest (slot count)
     * @returns {number}
     */
    getCombatLootSlotCount() {
        return Object.keys(this.getCombatLootChest()).length;
    }

    /**
     * Get capacity of combat loot chest (base + architecting bonuses)
     * @returns {number}
     */
    getCombatLootCapacity() {
        const baseCapacity = 20;
        const archBonus = this.getCombatVaultBonus();
        return baseCapacity + archBonus;
    }

    /**
     * Get architecting bonus for combat vault capacity
     * @returns {number}
     */
    getCombatVaultBonus() {
        // Check architecting milestones for combat vault upgrades
        const purchased = this.player.architectingMilestones || [];
        let bonus = 0;
        if (purchased.includes('combat_vault_1')) bonus += 5;
        if (purchased.includes('combat_vault_2')) bonus += 10;
        if (purchased.includes('combat_vault_3')) bonus += 15;
        return bonus;
    }

    /**
     * Check if combat loot chest has space for new item
     * @param {string} itemId - Item to check (existing items can always stack)
     * @returns {boolean}
     */
    hasCombatLootSpace(itemId = null) {
        const chest = this.getCombatLootChest();
        // If item already exists, infinite stacking means it always has space
        if (itemId && chest[itemId] !== undefined) return true;
        // Otherwise check slot count vs capacity
        return this.getCombatLootSlotCount() < this.getCombatLootCapacity();
    }

    /**
     * Add loot to combat chest
     * @param {string} itemId - Item identifier
     * @param {number} quantity - Amount to add
     * @returns {{ success: boolean, added: number, reason?: string }}
     */
    addCombatLoot(itemId, quantity = 1) {
        const chest = this.getCombatLootChest();

        // Check if this is a new slot
        if (chest[itemId] === undefined) {
            if (!this.hasCombatLootSpace()) {
                // Auto-collect oldest item to make room
                const oldestItemId = Object.keys(chest)[0];
                if (oldestItemId) {
                    const oldestQty = chest[oldestItemId];
                    const result = this.addItem(oldestItemId, oldestQty);
                    if (result.success) {
                        delete chest[oldestItemId];
                    } else {
                        // Both chest and inventory full — new loot stacks on existing chest slots only
                        return { success: false, added: 0, reason: 'Loot chest and inventory full' };
                    }
                }
            }
        }

        // Add to chest (infinite stacking)
        chest[itemId] = (chest[itemId] || 0) + quantity;
        return { success: true, added: quantity };
    }

    /**
     * Get quantity of item in combat loot chest
     * @param {string} itemId - Item identifier
     * @returns {number}
     */
    getCombatLootCount(itemId) {
        const chest = this.getCombatLootChest();
        return chest[itemId] || 0;
    }

    /**
     * Collect item from combat loot chest to inventory
     * @param {string} itemId - Item identifier
     * @param {number} quantity - Amount to collect (default all)
     * @returns {{ success: boolean, collected: number, reason?: string }}
     */
    collectCombatLoot(itemId, quantity = null) {
        const chest = this.getCombatLootChest();
        const available = chest[itemId] || 0;

        if (available === 0) {
            return { success: false, collected: 0, reason: 'Item not in chest' };
        }

        const toCollect = quantity === null ? available : Math.min(quantity, available);

        // Add to inventory
        const addResult = this.addItem(itemId, toCollect);
        if (!addResult.success) {
            return { success: false, collected: 0, reason: addResult.reason };
        }

        // Remove from chest
        chest[itemId] -= addResult.added;
        if (chest[itemId] <= 0) {
            delete chest[itemId];
        }

        return { success: true, collected: addResult.added };
    }

    /**
     * Collect all items from combat loot chest to inventory
     * @returns {{ success: boolean, collectedItems: object[] }}
     */
    collectAllCombatLoot() {
        const chest = this.getCombatLootChest();
        const collectedItems = [];

        for (const [itemId, quantity] of Object.entries({ ...chest })) {
            const result = this.collectCombatLoot(itemId, quantity);
            if (result.success && result.collected > 0) {
                collectedItems.push({ itemId, quantity: result.collected });
            }
        }

        return { success: true, collectedItems };
    }

    /**
     * Get total value of items in combat loot chest
     * @returns {number}
     */
    getCombatLootValue() {
        const chest = this.getCombatLootChest();
        let total = 0;
        for (const [itemId, quantity] of Object.entries(chest)) {
            const item = getItem(itemId);
            if (item?.value) {
                total += item.value * quantity;
            }
        }
        return total;
    }

    // ==================== PLAYER CONTRACT METHODS ====================

    /**
     * Get all active player contracts
     * @returns {Array} Active contracts array
     */
    getActiveContracts() {
        return this.player.activeContracts;
    }

    /**
     * Add a contract to active contracts
     * @param {object} contract - Contract object
     */
    addActiveContract(contract) {
        this.player.activeContracts.push(contract);
    }

    /**
     * Remove a contract by ID
     * @param {string} contractId - Contract identifier
     * @returns {boolean} Whether removal was successful
     */
    removeActiveContract(contractId) {
        const index = this.player.activeContracts.findIndex(c => c.id === contractId);
        if (index === -1) return false;
        this.player.activeContracts.splice(index, 1);
        return true;
    }

    /**
     * Update progress for a contract requirement
     * @param {string} contractId - Contract identifier
     * @param {string} itemId - Item identifier for the requirement
     * @param {number} amount - Amount to add to progress
     * @returns {boolean} Whether update was successful
     */
    updateContractProgress(contractId, itemId, amount) {
        const contract = this.player.activeContracts.find(c => c.id === contractId);
        if (!contract) return false;

        // Find the requirement for this item
        const requirement = contract.requirements?.find(r => r.itemId === itemId);
        if (!requirement) return false;

        // Update current progress
        requirement.current = (requirement.current || 0) + amount;
        return true;
    }

    // ==================== BOUNTY CONTRACT METHODS ====================

    /**
     * Get all active bounty contracts
     * @returns {Array} Active bounty contracts array
     */
    getBountyContracts() {
        // Bounty contracts are stored separately from pet/player contracts
        // Initialize if not exists (for save compatibility)
        if (!this.player.bountyContracts) {
            this.player.bountyContracts = [];
        }
        return this.player.bountyContracts;
    }

    /**
     * Add a bounty contract
     * @param {object} contract - Contract object with contractId, progress, etc.
     */
    addBountyContract(contract) {
        if (!this.player.bountyContracts) {
            this.player.bountyContracts = [];
        }
        this.player.bountyContracts.push(contract);
    }

    /**
     * Remove a bounty contract by ID
     * @param {string} contractId - Contract identifier
     * @returns {boolean} Whether removal was successful
     */
    removeBountyContract(contractId) {
        if (!this.player.bountyContracts) return false;
        const index = this.player.bountyContracts.findIndex(c => c.contractId === contractId);
        if (index === -1) return false;
        this.player.bountyContracts.splice(index, 1);
        return true;
    }

    /**
     * Get pinned contract ID (Dedicated Supplier talent)
     * @returns {string|null} Pinned contract ID or null
     */
    getPinnedContract() {
        return this.player.pinnedContract || null;
    }

    /**
     * Set pinned contract (Dedicated Supplier talent)
     * @param {string} contractId - Contract ID to pin
     */
    setPinnedContract(contractId) {
        this.player.pinnedContract = contractId;
    }

    /**
     * Clear pinned contract
     */
    clearPinnedContract() {
        this.player.pinnedContract = null;
    }

    // ==================== GOLD UTILITY METHODS ====================

    /**
     * Get current gold (coins) count
     * @returns {number}
     */
    getGold() {
        return this.getItemCount('coins');
    }

    /**
     * Add gold to inventory
     * @param {number} amount - Amount to add
     * @returns {{ success: boolean, added: number, reason?: string }}
     */
    addGold(amount) {
        // Apply Coin Magnet talent (+10% coins from all sources)
        const coinBonus = getTalentFlatBonus(SKILLS.PILFERING, 'globalCoinBonus') || 0;
        const adjustedAmount = coinBonus > 0 ? Math.floor(amount * (1 + coinBonus)) : amount;
        return this.addItem('coins', adjustedAmount);
    }

    /**
     * Remove gold from inventory
     * @param {number} amount - Amount to remove
     * @returns {boolean} Whether removal was successful
     */
    removeGold(amount) {
        return this.removeItem('coins', amount);
    }

    // ==================== DAILY SHOP METHODS ====================

    /**
     * Get daily shop stock for a given dailyId
     * Resets if 24h have passed since last reset
     * @param {string} dailyId - The daily stock identifier
     * @param {number} maxStock - Maximum stock when reset
     * @returns {{ remaining: number, resetTime: number }}
     */
    getDailyShopStock(dailyId, maxStock) {
        if (!this.player.dailyShop) {
            this.player.dailyShop = {};
        }
        const entry = this.player.dailyShop[dailyId];
        if (!entry) {
            // First time: full stock, no timer yet (starts on first purchase)
            this.player.dailyShop[dailyId] = {
                remaining: maxStock,
                resetTime: 0
            };
        } else if (entry.resetTime > 0 && (Date.now() >= entry.resetTime || entry.resetTime > Date.now() + 25 * 60 * 60 * 1000)) {
            // Timer expired or anomalous (clock manipulation): reset stock, clear timer
            this.player.dailyShop[dailyId] = {
                remaining: maxStock,
                resetTime: 0
            };
        }
        return this.player.dailyShop[dailyId];
    }

    /**
     * Consume daily shop stock
     * @param {string} dailyId - The daily stock identifier
     * @param {number} quantity - Amount to consume
     * @returns {boolean} Whether consumption was successful
     */
    consumeDailyShopStock(dailyId, quantity) {
        const entry = this.player.dailyShop[dailyId];
        if (!entry || entry.remaining < quantity) return false;
        entry.remaining -= quantity;
        // Start 24h restock timer on first purchase
        if (entry.resetTime === 0) {
            entry.resetTime = Date.now() + 24 * 60 * 60 * 1000;
        }
        return true;
    }

    // ==================== GATHERER ZONE METHODS ====================

    /**
     * Get current gatherer zone
     * @returns {string|null} Zone ID or null
     */
    getGathererZone() {
        return this.player.gathererZone;
    }

    /**
     * Set current gatherer zone
     * @param {string|null} zoneId - Zone ID or null to clear
     */
    setGathererZone(zoneId) {
        this.player.gathererZone = zoneId;
    }

    // ==================== CORRUPTION METHODS ====================

    /**
     * Get current corruption state
     * Handles migration from old single-skill format to new multi-skill format
     * @returns {Object} Corruption state object with skills property
     */
    getCorruption() {
        // Initialize if not exists
        if (!this.player.corruption) {
            this.player.corruption = { skills: {} };
        }

        // Migrate old single-skill format to new multi-skill format
        if (this.player.corruption.active !== undefined && this.player.corruption.skillId) {
            // Old format had: { active, skillId, remainingMs, startedAt, extended }
            const oldState = this.player.corruption;
            if (oldState.active && oldState.skillId) {
                this.player.corruption = {
                    skills: {
                        [oldState.skillId]: {
                            active: true,
                            startedAt: oldState.startedAt,
                            remainingMs: oldState.remainingMs,
                            extended: oldState.extended || false,
                        }
                    }
                };
            } else {
                // Old format but not active
                this.player.corruption = { skills: {} };
            }
        }

        // Ensure skills object exists
        if (!this.player.corruption.skills) {
            this.player.corruption.skills = {};
        }

        return this.player.corruption;
    }

    /**
     * Set corruption state for a specific skill
     * @param {string} skillId - Skill identifier
     * @param {Object} state - Corruption state for the skill
     */
    setSkillCorruption(skillId, state) {
        const corruption = this.getCorruption();
        corruption.skills[skillId] = state;
    }

    /**
     * Set entire corruption state (legacy method)
     * @param {Object} corruptionState - New corruption state
     */
    setCorruption(corruptionState) {
        this.player.corruption = corruptionState;
    }

    // ==================== PYRE VEIL METHODS ====================

    /**
     * Get Pyre Veil state, initializing if needed
     * @returns {Object} pyreVeil state object
     */
    getPyreVeil() {
        if (!this.player.pyreVeil) {
            this.player.pyreVeil = {
                seenLore: [],
                loreQueue: [],
                discoveredMobs: []
            };
        }
        return this.player.pyreVeil;
    }

    /**
     * Check if lore has been seen
     * @param {string} loreId - Lore identifier (e.g., 'pyre_15')
     * @returns {boolean}
     */
    hasSeenLore(loreId) {
        const pyreVeil = this.getPyreVeil();
        return pyreVeil.seenLore.includes(loreId);
    }

    /**
     * Mark lore as seen
     * @param {string} loreId - Lore identifier
     */
    markLoreSeen(loreId) {
        const pyreVeil = this.getPyreVeil();
        if (!pyreVeil.seenLore.includes(loreId)) {
            pyreVeil.seenLore.push(loreId);
        }
    }

    /**
     * Queue lore for display after offline modal
     * @param {string} loreId - Lore identifier
     */
    queueLore(loreId) {
        const pyreVeil = this.getPyreVeil();
        if (!this.hasSeenLore(loreId) && !pyreVeil.loreQueue.includes(loreId)) {
            pyreVeil.loreQueue.push(loreId);
        }
    }

    /**
     * Get and clear the lore queue
     * @returns {string[]} Array of lore IDs to show
     */
    popLoreQueue() {
        const pyreVeil = this.getPyreVeil();
        const queue = [...pyreVeil.loreQueue];
        pyreVeil.loreQueue = [];
        return queue;
    }

    /**
     * Check if a hidden mob has been discovered (for first-time reveal animation)
     * @param {string} mobId - Mob ID
     * @returns {boolean}
     */
    hasDiscoveredMob(mobId) {
        const pyreVeil = this.getPyreVeil();
        return pyreVeil.discoveredMobs.includes(mobId);
    }

    /**
     * Mark a hidden mob as discovered
     * @param {string} mobId - Mob ID
     */
    markMobDiscovered(mobId) {
        const pyreVeil = this.getPyreVeil();
        if (!pyreVeil.discoveredMobs.includes(mobId)) {
            pyreVeil.discoveredMobs.push(mobId);
        }
    }

    // ==================== TALENT TREE METHODS ====================

    /**
     * Get talent tree data for a skill
     * @param {string} skillId - Skill identifier
     * @returns {Object} Talent tree state { nodes: {nodeId: rank}, spentPoints }
     */
    getTalentTree(skillId) {
        // Initialize if not exists
        if (!this.player.talentTrees) {
            this.player.talentTrees = {};
        }
        if (!this.player.talentTrees[skillId]) {
            this.player.talentTrees[skillId] = {
                nodes: {},
                spentPoints: 0
            };
        }
        return this.player.talentTrees[skillId];
    }

    /**
     * Get all talent trees
     * @returns {Object} All talent tree states
     */
    getAllTalentTrees() {
        if (!this.player.talentTrees) {
            this.player.talentTrees = {};
        }
        return this.player.talentTrees;
    }

    /**
     * Set a talent node's rank
     * @param {string} skillId - Skill identifier
     * @param {string} nodeId - Node identifier
     * @param {number} rank - New rank value
     */
    setTalentNodeRank(skillId, nodeId, rank) {
        const tree = this.getTalentTree(skillId);
        const oldRank = tree.nodes[nodeId] || 0;
        tree.nodes[nodeId] = rank;

        // Update spent points
        tree.spentPoints += (rank - oldRank);
    }

    /**
     * Reset all talent nodes in a skill tree
     * @param {string} skillId - Skill identifier
     */
    resetTalentTree(skillId) {
        if (this.player.talentTrees && this.player.talentTrees[skillId]) {
            this.player.talentTrees[skillId] = {
                nodes: {},
                spentPoints: 0
            };
        }
    }

    /**
     * Check if player has any talent points available for a skill
     * @param {string} skillId - Skill identifier
     * @returns {boolean} True if has unspent points
     */
    hasTalentPointsAvailable(skillId) {
        // Import dynamically to avoid circular dependency
        // This is just a helper - the actual calculation is in talentTree.js
        const tree = this.getTalentTree(skillId);
        const skill = this.getSkill(skillId);
        if (!skill) return false;

        // Rough estimate: 1 point per 5 levels, capped at level 90
        const earnedPoints = Math.floor(Math.min(skill.level, 90) / 5);
        return earnedPoints > tree.spentPoints;
    }

    // ==================== COMBAT METHODS ====================

    /**
     * Get combat state
     * @returns {Object} Combat state object
     */
    getCombat() {
        // Initialize if not exists (for save compatibility)
        if (!this.player.combat) {
            this.player.combat = {
                currentHp: this.getMaxHp(), // Use dynamic HP based on combat level
                inCombat: false,
                currentZone: null,
                currentMob: null,
                mobCurrentHp: 0,
                lastPlayerAttack: 0,
                lastMobAttack: 0,
                lastEatTime: 0,
                killCounts: {},
                totalKills: 0,
                killHistory: [],
                isDead: false,
                deathTime: null,
                autoEat: {
                    unlocked: false,
                    enabled: false,
                    storedFood: null,
                    threshold: 50
                }
            };
        }
        // Ensure killHistory exists for older saves
        if (!this.player.combat.killHistory) {
            this.player.combat.killHistory = [];
        }
        // Ensure zoneProgress exists for older saves
        if (!this.player.combat.zoneProgress) {
            this.player.combat.zoneProgress = {};
        }
        // Ensure selectedSpell exists for older saves (spell persistence)
        if (this.player.combat.selectedSpell === undefined) {
            this.player.combat.selectedSpell = null;
        }
        // Ensure potionBuffs exists for older saves (potion system)
        if (!this.player.combat.potionBuffs) {
            this.player.combat.potionBuffs = {};
        }
        // Ensure apothecaryBelt exists for older saves (auto-potion system)
        if (!this.player.combat.apothecaryBelt) {
            this.player.combat.apothecaryBelt = {
                unlocked: false,
                enabled: false,
                slots: {
                    melee: [null, null, null, null],
                    ranged: [null, null, null, null],
                    magic: [null, null, null, null]
                }
            };
        }
        // Migrate old flat array slots to per-set slots
        if (Array.isArray(this.player.combat.apothecaryBelt.slots)) {
            const oldSlots = this.player.combat.apothecaryBelt.slots;
            // Return old potions to inventory/bank
            for (const slot of oldSlots) {
                if (slot && slot.itemId && slot.quantity > 0) {
                    const addResult = this.addItem(slot.itemId, slot.quantity);
                    if (!addResult || !addResult.success) {
                        // Inventory full, try bank
                        this.addBankItem(slot.itemId, slot.quantity);
                    }
                }
            }
            // Initialize per-set structure
            this.player.combat.apothecaryBelt.slots = {
                melee: [null, null, null, null],
                ranged: [null, null, null, null],
                magic: [null, null, null, null]
            };
        }
        return this.player.combat;
    }

    /**
     * Get current HP
     * @returns {number}
     */
    getCurrentHp() {
        return this.getCombat().currentHp;
    }

    /**
     * Get max HP (scales from 10 at level 1 to 99 at level 99, capped at 99 for virtual levels)
     * Includes Vital Force talent bonus (+5 flat max HP per rank)
     * Includes equipment HP bonus from offhand (bulwarks)
     * @returns {number}
     */
    getMaxHp() {
        const combatLevel = Math.min(this.getSkill(SKILLS.COMBAT)?.level || 1, 99);
        // Formula: 10 HP at level 1, scaling linearly to 99 HP at level 99
        const hpRange = COMBAT_CONFIG.MAX_HP - COMBAT_CONFIG.MIN_HP; // 89
        const baseHp = Math.floor(COMBAT_CONFIG.MIN_HP + (combatLevel - 1) * (hpRange / 98));

        // Apply Vital Force talent bonus (+5 flat max HP per rank)
        const maxHpBonus = getTalentFlatBonus(SKILLS.COMBAT, 'maxHp');

        // Apply equipment HP bonus from offhand (bulwarks)
        let equipmentHpBonus = 0;
        const gear = this.getEquippedGear();
        if (gear.offhand) {
            const offhand = getItem(gear.offhand);
            if (offhand?.hpBonus) {
                equipmentHpBonus += offhand.hpBonus;
            }
        }

        return baseHp + Math.floor(maxHpBonus) + equipmentHpBonus;
    }

    /**
     * Set current HP (clamped between 0 and max)
     * @param {number} hp - New HP value
     */
    setCurrentHp(hp) {
        const combat = this.getCombat();
        const maxHp = this.getMaxHp();
        combat.currentHp = Math.max(0, Math.min(hp, maxHp));
    }

    /**
     * Heal HP by amount
     * @param {number} amount - Amount to heal
     * @returns {number} Actual amount healed
     */
    healHp(amount) {
        const combat = this.getCombat();
        const maxHp = this.getMaxHp();
        const before = combat.currentHp;
        combat.currentHp = Math.min(combat.currentHp + amount, maxHp);
        return combat.currentHp - before;
    }

    /**
     * Take damage
     * @param {number} amount - Damage amount
     * @returns {{ died: boolean, damage: number }}
     */
    takeDamage(amount) {
        const combat = this.getCombat();
        const actualDamage = Math.min(amount, combat.currentHp);
        combat.currentHp -= actualDamage;

        if (combat.currentHp <= 0) {
            combat.isDead = true;
            combat.deathTime = Date.now();
            combat.inCombat = false;
            return { died: true, damage: actualDamage };
        }
        return { died: false, damage: actualDamage };
    }

    /**
     * Check if player is dead
     * @returns {boolean}
     */
    isDead() {
        return this.getCombat().isDead;
    }

    /**
     * Respawn player (reset HP, clear death state)
     */
    respawn() {
        const combat = this.getCombat();
        combat.currentHp = this.getMaxHp();
        combat.isDead = false;
        combat.deathTime = null;
        combat.inCombat = false;
        combat.currentMob = null;
        combat.mobCurrentHp = 0;
    }

    /**
     * Get respawn time remaining in ms
     * @returns {number} Time remaining or 0 if can respawn
     */
    getRespawnTimeRemaining() {
        const combat = this.getCombat();
        if (!combat.isDead || !combat.deathTime) return 0;

        const elapsed = Math.max(0, Date.now() - combat.deathTime);
        return Math.max(0, COMBAT_CONFIG.RESPAWN_TIME - elapsed);
    }

    /**
     * Track a mob kill
     * @param {string} mobId - Mob identifier
     */
    trackKill(mobId) {
        const combat = this.getCombat();
        combat.killCounts[mobId] = (combat.killCounts[mobId] || 0) + 1;
        combat.totalKills++;

        // Track for kills/hour calculation
        if (!combat.killHistory) combat.killHistory = [];
        combat.killHistory.push({ timestamp: Date.now(), mobId });

        // Prune history older than 1 hour to prevent memory bloat
        const oneHourAgo = Date.now() - 3600000;
        combat.killHistory = combat.killHistory.filter(k => k.timestamp > oneHourAgo);
    }

    /**
     * Get kill count for a specific mob
     * @param {string} mobId - Mob identifier
     * @returns {number}
     */
    getKillCount(mobId) {
        return this.getCombat().killCounts[mobId] || 0;
    }

    /**
     * Track loot received from a specific mob (for Bestiary)
     * @param {string} mobId - Mob identifier
     * @param {string} itemId - Item identifier
     * @param {number} quantity - Amount received
     */
    trackMobLoot(mobId, itemId, quantity) {
        const combat = this.getCombat();
        if (!combat.mobLootHistory) combat.mobLootHistory = {};
        if (!combat.mobLootHistory[mobId]) combat.mobLootHistory[mobId] = {};
        combat.mobLootHistory[mobId][itemId] = (combat.mobLootHistory[mobId][itemId] || 0) + quantity;
    }

    /**
     * Get loot history for a specific mob
     * @param {string} mobId - Mob identifier
     * @returns {Object} Map of itemId to total quantity received
     */
    getMobLootHistory(mobId) {
        return this.getCombat().mobLootHistory?.[mobId] || {};
    }

    /**
     * Get total kills across all mobs
     * @returns {number}
     */
    getTotalKills() {
        return this.getCombat().totalKills;
    }

    /**
     * Get kills per hour based on recent kill history
     * @returns {number}
     */
    getKillsPerHour() {
        const combat = this.getCombat();
        if (!combat.killHistory || combat.killHistory.length === 0) return 0;

        const oneHourAgo = Date.now() - 3600000;
        const recentKills = combat.killHistory.filter(k => k.timestamp > oneHourAgo);

        // If we have less than an hour of data, extrapolate
        if (recentKills.length > 0) {
            const oldestKill = recentKills[0].timestamp;
            const timeSpan = Date.now() - oldestKill;
            if (timeSpan < 60000) {
                // Less than 1 minute of data - too early to estimate
                return recentKills.length * 60; // Rough estimate per minute * 60
            }
            // Extrapolate to full hour
            return Math.floor(recentKills.length * (3600000 / timeSpan));
        }
        return 0;
    }

    /**
     * Get the selected spell ID (persisted across sessions)
     * @returns {string|null} Spell ID or null
     */
    getSelectedSpell() {
        return this.getCombat().selectedSpell;
    }

    /**
     * Set the selected spell ID (persisted across sessions)
     * @param {string|null} spellId - Spell ID or null to clear
     */
    setSelectedSpell(spellId) {
        this.getCombat().selectedSpell = spellId;
    }

    // ==================== ZONE PROGRESS METHODS ====================

    /**
     * Get zone progress for a specific zone (initializes if needed)
     * @param {string} zoneId - Zone identifier
     * @returns {{ cleared: boolean, killsAtClear: number, clearedAt: number|null, lifetimeKills: number }}
     */
    getZoneProgress(zoneId) {
        const combat = this.getCombat();
        if (!combat.zoneProgress[zoneId]) {
            combat.zoneProgress[zoneId] = {
                cleared: false,
                killsAtClear: 0,
                clearedAt: null,
                lifetimeKills: 0
            };
        }
        return combat.zoneProgress[zoneId];
    }

    /**
     * Check if a zone has been cleared (enables target farming)
     * @param {string} zoneId - Zone identifier
     * @returns {boolean}
     */
    isZoneCleared(zoneId) {
        return this.getZoneProgress(zoneId).cleared;
    }

    /**
     * Mark a zone as cleared
     * @param {string} zoneId - Zone identifier
     * @param {number} killCount - Number of kills at time of clearing
     */
    clearZone(zoneId, killCount) {
        const progress = this.getZoneProgress(zoneId);
        progress.cleared = true;
        progress.killsAtClear = killCount;
        progress.clearedAt = Date.now();
    }

    /**
     * Increment lifetime kills for a zone
     * @param {string} zoneId - Zone identifier
     * @returns {number} New lifetime kill count
     */
    incrementZoneLifetimeKills(zoneId) {
        const progress = this.getZoneProgress(zoneId);
        progress.lifetimeKills++;
        return progress.lifetimeKills;
    }

    /**
     * Get lifetime kills for a zone
     * @param {string} zoneId - Zone identifier
     * @returns {number}
     */
    getZoneLifetimeKills(zoneId) {
        return this.getZoneProgress(zoneId).lifetimeKills;
    }

    // ==================== BOSS KILL TRACKING ====================

    /**
     * Record a boss kill
     * @param {string} bossId - Boss mob identifier
     */
    recordBossKill(bossId) {
        const combat = this.getCombat();
        if (!combat.bossKills) combat.bossKills = {};
        combat.bossKills[bossId] = (combat.bossKills[bossId] || 0) + 1;
    }

    /**
     * Get total kills for a specific boss
     * @param {string} bossId - Boss mob identifier
     * @returns {number}
     */
    getBossKillCount(bossId) {
        const combat = this.getCombat();
        if (!combat.bossKills) combat.bossKills = {};
        return combat.bossKills[bossId] || 0;
    }

    /**
     * Check if a boss has ever been defeated
     * @param {string} bossId - Boss mob identifier
     * @returns {boolean}
     */
    hasDefeatedBoss(bossId) {
        return this.getBossKillCount(bossId) > 0;
    }

    /**
     * Check if all bosses in a list have been defeated at least once
     * @param {string[]} bossIds - Array of boss mob identifiers
     * @returns {boolean}
     */
    hasDefeatedAllBosses(bossIds) {
        return bossIds.every(id => this.hasDefeatedBoss(id));
    }

    // ==================== EQUIPMENT METHODS ====================

    /**
     * Get equipped gear - returns the active set's gear
     * Each set (melee, ranged, magic) has its own independent equipment storage
     * @returns {Object} Equipped gear object
     */
    getEquippedGear() {
        // Ensure sets exist
        const sets = this.getEquipmentSets();

        // Default to melee if no active set
        if (!this.player.activeEquipmentSet) {
            this.player.activeEquipmentSet = 'melee';
        }

        const activeSet = sets[this.player.activeEquipmentSet];

        // Ensure ammo slot exists for older saves
        if (!('ammo' in activeSet.gear)) {
            activeSet.gear.ammo = null;
        }

        return activeSet.gear;
    }

    /**
     * Get equipped item in a slot
     * @param {string} slot - Slot name (weapon, head, body, etc.)
     * @returns {string|null} Item ID or null
     */
    getEquippedItem(slot) {
        return this.getEquippedGear()[slot] || null;
    }

    /**
     * Determine which equipment set an item belongs to based on its style
     * @param {Object} itemData - Item data object
     * @returns {string|null} 'melee', 'ranged', 'magic', or null if universal
     */
    getItemCombatStyle(itemData) {
        if (!itemData) return null;

        // Check attackStyle for weapons
        if (itemData.attackStyle) {
            return itemData.attackStyle.toLowerCase();
        }

        // Check armorType for armor
        if (itemData.armorType) {
            return itemData.armorType.toLowerCase();
        }

        // Check for accuracy types to infer style (use !== undefined to handle 0 values)
        const hasMelee = itemData.meleeAccuracy !== undefined;
        const hasRanged = itemData.rangedAccuracy !== undefined;
        const hasMagic = itemData.magicAccuracy !== undefined;
        if (hasMelee && !hasRanged && !hasMagic) return 'melee';
        if (hasRanged && !hasMelee && !hasMagic) return 'ranged';
        if (hasMagic && !hasMelee && !hasRanged) return 'magic';

        return null; // Universal item (rings, some accessories)
    }

    /**
     * Equip an item to a slot
     * Items are equipped to their matching set (melee gear → melee set, etc.)
     * @param {string} slot - Slot name
     * @param {string} itemId - Item ID to equip
     * @returns {{ success: boolean, unequipped?: string, reason?: string, targetSet?: string }}
     */
    equipItem(slot, itemId) {
        const sets = this.getEquipmentSets();
        const activeSetId = this.player.activeEquipmentSet || 'melee';

        // Validate slot exists
        if (!(slot in sets.melee.gear)) {
            return { success: false, reason: 'Invalid slot' };
        }

        // Check if item is in inventory
        if (!this.getItemCount(itemId)) {
            return { success: false, reason: 'Item not in inventory' };
        }

        const itemData = getItem(itemId);

        // Check combat level requirement
        if (itemData?.combatLevel) {
            const combatLevel = this.getSkill(SKILLS.COMBAT)?.level || 1;
            if (combatLevel < itemData.combatLevel) {
                return { success: false, reason: `Requires Combat level ${itemData.combatLevel}` };
            }
        }

        // Determine target set based on item style
        // Ammo always goes to ranged, other items go to their matching style or active set
        let targetSetId;
        if (slot === 'ammo') {
            targetSetId = 'ranged'; // Ammo always goes to ranged set
        } else {
            const itemStyle = this.getItemCombatStyle(itemData);
            targetSetId = itemStyle || activeSetId; // Use item's style or fall back to active set
        }

        const targetSet = sets[targetSetId];
        const gear = targetSet.gear;

        // Check if trying to equip to offhand while 2H weapon is equipped in target set
        if (slot === 'offhand') {
            const currentWeapon = gear.weapon;
            if (currentWeapon) {
                const weaponData = getItem(currentWeapon);
                if (weaponData?.twoHanded) {
                    return { success: false, reason: 'Cannot equip offhand with a two-handed weapon' };
                }
            }
        }

        const previousItem = gear[slot];
        let unequippedOffhand = null;

        // If equipping a 2H weapon, auto-unequip offhand first
        if (slot === 'weapon' && itemData?.twoHanded && gear.offhand) {
            unequippedOffhand = gear.offhand;
            this.addItem(gear.offhand, 1);
            gear.offhand = null;
        }

        // Ammo slot is special - store quantity per set, add to existing stack if same type
        if (slot === 'ammo') {
            const inventoryQty = this.getItemCount(itemId);
            if (inventoryQty <= 0) {
                return { success: false, reason: 'No ammo in inventory' };
            }

            // Remove all from inventory
            this.removeItem(itemId, inventoryQty);

            // If same ammo type already equipped, add to it
            if (gear.ammo === itemId) {
                targetSet.ammoQuantity = (targetSet.ammoQuantity || 0) + inventoryQty;
            } else {
                // Different type - return old ammo to inventory first
                if (previousItem && targetSet.ammoQuantity > 0) {
                    this.addItem(previousItem, targetSet.ammoQuantity);
                }
                gear.ammo = itemId;
                targetSet.ammoQuantity = inventoryQty;
            }
            return { success: true, unequipped: previousItem, targetSet: targetSetId };
        }

        // Remove item from inventory
        this.removeItem(itemId, 1);

        // If there was an item equipped, put it back in inventory
        if (previousItem) {
            this.addItem(previousItem, 1);
        }

        // Equip new item
        gear[slot] = itemId;

        return { success: true, unequipped: previousItem, unequippedOffhand, targetSet: targetSetId };
    }

    /**
     * Unequip an item from a slot
     * @param {string} slot - Slot name
     * @returns {{ success: boolean, itemId?: string, reason?: string }}
     */
    unequipItem(slot) {
        const gear = this.getEquippedGear();
        if (!(slot in gear)) {
            return { success: false, reason: 'Invalid slot' };
        }

        const itemId = gear[slot];
        if (!itemId) {
            return { success: false, reason: 'Slot is empty' };
        }

        // Ammo slot is special - return ammo stack to inventory (from active set)
        if (slot === 'ammo') {
            const sets = this.getEquipmentSets();
            const activeSetId = this.player.activeEquipmentSet || 'melee';
            const activeSet = sets[activeSetId];

            const qty = activeSet?.ammoQuantity || 0;
            if (qty > 0) {
                this.addItem(itemId, qty);
            }
            gear[slot] = null;
            activeSet.ammoQuantity = 0;
            return { success: true, itemId };
        }

        // Check if inventory has space
        if (!this.hasInventorySpace() && !this.findInventorySlot(itemId)) {
            return { success: false, reason: 'Inventory full' };
        }

        // Add item back to inventory
        this.addItem(itemId, 1);
        gear[slot] = null;

        return { success: true, itemId };
    }

    /**
     * Check if currently equipped weapon is two-handed
     * @returns {boolean}
     */
    hasEquippedTwoHandedWeapon() {
        const gear = this.getEquippedGear();
        if (!gear.weapon) return false;
        const weaponData = getItem(gear.weapon);
        return weaponData?.twoHanded === true;
    }

    /**
     * Get equipped ammo info (from active set)
     * @returns {{ itemId: string|null, quantity: number }}
     */
    getEquippedAmmo() {
        const gear = this.getEquippedGear();
        const sets = this.getEquipmentSets();
        const activeSetId = this.player.activeEquipmentSet || 'melee';
        const activeSet = sets[activeSetId];

        return {
            itemId: gear.ammo || null,
            quantity: activeSet?.ammoQuantity || 0
        };
    }

    /**
     * Consume ammo from equipped slot (in active set)
     * Includes ammo efficiency check from quiver (back slot)
     * @param {number} amount - Amount to consume
     * @returns {{ consumed: boolean, saved: boolean }} consumed = had enough ammo, saved = ammo efficiency proc
     */
    consumeAmmo(amount = 1) {
        const gear = this.getEquippedGear();
        if (!gear.ammo) return { consumed: false, saved: false };

        const sets = this.getEquipmentSets();
        const activeSetId = this.player.activeEquipmentSet || 'melee';
        const activeSet = sets[activeSetId];

        const currentQty = activeSet?.ammoQuantity || 0;
        if (currentQty < amount) return { consumed: false, saved: false };

        // Check ammo efficiency from quiver (back slot)
        let saved = false;
        if (gear.back) {
            const backItem = getItem(gear.back);
            if (backItem?.ammoEfficiency && Math.random() * 100 < backItem.ammoEfficiency) {
                saved = true;
                // Ammo not consumed due to quiver efficiency
                return { consumed: true, saved: true };
            }
        }

        activeSet.ammoQuantity = currentQty - amount;

        // Clear ammo slot if empty
        if (activeSet.ammoQuantity <= 0) {
            gear.ammo = null;
            activeSet.ammoQuantity = 0;
        }

        return { consumed: true, saved: false };
    }

    // ==================== EQUIPMENT SETS METHODS ====================

    /**
     * Get all equipment sets
     * @returns {Object} Equipment sets object
     */
    getEquipmentSets() {
        // Initialize if not exists (for save compatibility)
        if (!this.player.equipmentSets) {
            this.player.equipmentSets = {
                melee: { name: 'Melee', icon: '⚔️', gear: { weapon: null, offhand: null, back: null, head: null, neck: null, shoulders: null, hands: null, body: null, legs: null, ring: null, feet: null, ammo: null }, ammoQuantity: 0 },
                ranged: { name: 'Ranged', icon: '🏹', gear: { weapon: null, offhand: null, back: null, head: null, neck: null, shoulders: null, hands: null, body: null, legs: null, ring: null, feet: null, ammo: null }, ammoQuantity: 0 },
                magic: { name: 'Magic', icon: '✨', gear: { weapon: null, offhand: null, back: null, head: null, neck: null, shoulders: null, hands: null, body: null, legs: null, ring: null, feet: null, ammo: null }, ammoQuantity: 0 }
            };
        }
        // Ensure ammo slot and ammoQuantity exist in all sets (for older saves)
        for (const setId of ['melee', 'ranged', 'magic']) {
            if (this.player.equipmentSets[setId]) {
                if (!('ammo' in this.player.equipmentSets[setId].gear)) {
                    this.player.equipmentSets[setId].gear.ammo = null;
                }
                // Ensure back slot exists (for older saves)
                if (!('back' in this.player.equipmentSets[setId].gear)) {
                    this.player.equipmentSets[setId].gear.back = null;
                }
                if (!('ammoQuantity' in this.player.equipmentSets[setId])) {
                    // Migrate old global ammoQuantity to this set if it has ammo equipped
                    if (this.player.equipmentSets[setId].gear.ammo && this.player.ammoQuantity) {
                        this.player.equipmentSets[setId].ammoQuantity = this.player.ammoQuantity;
                    } else {
                        this.player.equipmentSets[setId].ammoQuantity = 0;
                    }
                }
            }
        }
        return this.player.equipmentSets;
    }

    /**
     * Get active equipment set ID
     * @returns {string|null} Set ID or null if custom
     */
    getActiveEquipmentSet() {
        return this.player.activeEquipmentSet || null;
    }

    /**
     * Save current equipment to a set (legacy - just switches to set)
     * Since each set now stores gear independently, this just activates the set
     * @param {string} setId - 'melee', 'ranged', or 'magic'
     * @returns {{ success: boolean, reason?: string }}
     */
    saveEquipmentSet(setId) {
        return this.loadEquipmentSet(setId);
    }

    /**
     * Load equipment from a set
     * Simply switches to the specified set - each set stores its own gear independently
     * @param {string} setId - Set to load ('melee', 'ranged', or 'magic')
     * @returns {{ success: boolean, reason?: string }}
     */
    loadEquipmentSet(setId) {
        const sets = this.getEquipmentSets();
        if (!sets[setId]) {
            return { success: false, reason: 'Invalid set' };
        }

        // Just switch the active set - gear stays in each set
        this.player.activeEquipmentSet = setId;

        return { success: true, setName: sets[setId].name };
    }

    /**
     * Clear active equipment set marker (set to custom)
     */
    clearActiveEquipmentSet() {
        this.player.activeEquipmentSet = null;
    }

    // ==================== GLYPH POUCH METHODS ====================

    /**
     * Get glyph pouch (initializes if missing for save compatibility)
     * @returns {Object} Glyph pouch object with unlocked flag and slots array
     */
    getGlyphPouch() {
        // Migrate from old ammoPouch if exists
        if (this.player.ammoPouch && !this.player.glyphPouch) {
            this.player.glyphPouch = {
                unlocked: this.player.ammoPouch.unlocked || false,
                slots: []
            };
            // Migrate glyph slots only (skip arrow slots)
            for (const slot of (this.player.ammoPouch.slots || [])) {
                if (slot.type !== 'arrow') {
                    this.player.glyphPouch.slots.push({ itemId: slot.itemId, quantity: slot.quantity });
                }
            }
            // Ensure 6 slots
            while (this.player.glyphPouch.slots.length < 6) {
                this.player.glyphPouch.slots.push({ itemId: null, quantity: 0 });
            }
            delete this.player.ammoPouch;
        }

        if (!this.player.glyphPouch) {
            this.player.glyphPouch = {
                unlocked: false,
                slots: [
                    { itemId: null, quantity: 0 },
                    { itemId: null, quantity: 0 },
                    { itemId: null, quantity: 0 },
                    { itemId: null, quantity: 0 },
                    { itemId: null, quantity: 0 },
                    { itemId: null, quantity: 0 }
                ]
            };
        }
        // Ensure slots exist (for older saves)
        if (!this.player.glyphPouch.slots) {
            this.player.glyphPouch.slots = [
                { itemId: null, quantity: 0 },
                { itemId: null, quantity: 0 },
                { itemId: null, quantity: 0 },
                { itemId: null, quantity: 0 },
                { itemId: null, quantity: 0 },
                { itemId: null, quantity: 0 }
            ];
        }
        // Ensure 6 slots (old saves might have 5)
        while (this.player.glyphPouch.slots.length < 6) {
            this.player.glyphPouch.slots.push({ itemId: null, quantity: 0 });
        }
        return this.player.glyphPouch;
    }

    /**
     * Check if glyph pouch is unlocked
     * @returns {boolean}
     */
    isGlyphPouchUnlocked() {
        return this.getGlyphPouch().unlocked === true;
    }

    /**
     * Unlock the glyph pouch
     */
    unlockGlyphPouch() {
        this.getGlyphPouch().unlocked = true;
    }

    /**
     * Add glyphs to the glyph pouch
     * @param {string} itemId - Glyph item to add
     * @param {number} quantity - Amount to add
     * @returns {{ success: boolean, reason?: string, added?: number }}
     */
    addToGlyphPouch(itemId, quantity = 1) {
        if (!this.isGlyphPouchUnlocked()) {
            return { success: false, reason: 'Glyph pouch is locked' };
        }

        const pouch = this.getGlyphPouch();
        const item = getItem(itemId);
        if (!item) {
            return { success: false, reason: 'Invalid item' };
        }

        // Only glyphs can be stored
        if (item.category !== 'glyph') {
            return { success: false, reason: 'Only glyphs can be stored in the pouch' };
        }

        // Find existing slot with same item, or empty slot
        let targetSlot = null;
        let emptySlot = null;

        for (const slot of pouch.slots) {
            if (slot.itemId === itemId) {
                targetSlot = slot;
                break;
            }
            if (!slot.itemId && !emptySlot) {
                emptySlot = slot;
            }
        }

        if (!targetSlot && !emptySlot) {
            return { success: false, reason: 'No available slot in glyph pouch' };
        }

        // Check if player has the item in inventory
        const playerQty = this.getItemCount(itemId);
        if (playerQty < quantity) {
            return { success: false, reason: 'Not enough glyphs in inventory' };
        }

        // Use existing slot or empty slot
        const slot = targetSlot || emptySlot;

        // Remove from inventory
        this.removeItem(itemId, quantity);

        // Add to pouch slot
        if (!slot.itemId) {
            slot.itemId = itemId;
            slot.quantity = quantity;
        } else {
            slot.quantity += quantity;
        }

        return { success: true, added: quantity };
    }

    /**
     * Remove glyphs from pouch back to inventory
     * @param {number} slotIndex - Pouch slot index (0-5)
     * @param {number} quantity - Amount to remove (0 = all)
     * @returns {{ success: boolean, reason?: string, removed?: number }}
     */
    removeFromGlyphPouch(slotIndex, quantity = 0) {
        if (!this.isGlyphPouchUnlocked()) {
            return { success: false, reason: 'Glyph pouch is locked' };
        }

        const pouch = this.getGlyphPouch();
        if (slotIndex < 0 || slotIndex >= pouch.slots.length) {
            return { success: false, reason: 'Invalid slot index' };
        }

        const slot = pouch.slots[slotIndex];
        if (!slot.itemId || slot.quantity <= 0) {
            return { success: false, reason: 'Slot is empty' };
        }

        // Remove all if quantity is 0
        const toRemove = quantity <= 0 ? slot.quantity : Math.min(quantity, slot.quantity);

        // Add to inventory
        const addResult = this.addItem(slot.itemId, toRemove);
        if (!addResult.success) {
            return { success: false, reason: addResult.reason || 'Inventory full' };
        }

        // Remove from pouch
        slot.quantity -= toRemove;
        if (slot.quantity <= 0) {
            slot.itemId = null;
            slot.quantity = 0;
        }

        return { success: true, removed: toRemove };
    }

    /**
     * Check if player has enough glyphs for a spell (checks pouch if unlocked, else inventory)
     * @param {Array<{glyphId: string, quantity: number}>} glyphCost - Glyph requirements
     * @returns {{ success: boolean, reason?: string }}
     */
    hasGlyphsForSpell(glyphCost) {
        if (!glyphCost || glyphCost.length === 0) {
            return { success: true };
        }

        const pouch = this.getGlyphPouch();

        for (const cost of glyphCost) {
            let available = 0;

            // Check pouch first if unlocked
            if (this.isGlyphPouchUnlocked()) {
                for (const slot of pouch.slots) {
                    if (slot.itemId === cost.glyphId) {
                        available += slot.quantity;
                    }
                }
            }

            // Also check inventory (glyphs can be used from either location)
            available += this.getItemCount(cost.glyphId);

            if (available < cost.quantity) {
                const item = getItem(cost.glyphId);
                const name = item?.name || cost.glyphId;
                return { success: false, reason: `Missing ${name}: ${available}/${cost.quantity}` };
            }
        }

        return { success: true };
    }

    /**
     * Consume glyphs for casting a spell (draws from pouch first if unlocked, then inventory)
     * @param {Array<{glyphId: string, quantity: number}>} glyphCost - Glyph requirements
     * @returns {{ success: boolean, reason?: string }}
     */
    consumeGlyphsForSpell(glyphCost) {
        // First check if we have enough
        const check = this.hasGlyphsForSpell(glyphCost);
        if (!check.success) {
            return check;
        }

        const pouch = this.getGlyphPouch();

        for (const cost of glyphCost) {
            let remaining = cost.quantity;

            // Draw from pouch first if unlocked
            if (this.isGlyphPouchUnlocked()) {
                for (const slot of pouch.slots) {
                    if (slot.itemId === cost.glyphId && remaining > 0) {
                        const take = Math.min(slot.quantity, remaining);
                        slot.quantity -= take;
                        remaining -= take;

                        if (slot.quantity <= 0) {
                            slot.itemId = null;
                            slot.quantity = 0;
                        }
                    }
                }
            }

            // Draw remaining from inventory
            if (remaining > 0) {
                this.removeItem(cost.glyphId, remaining);
            }
        }

        return { success: true };
    }

    /**
     * Get total glyph count across pouch and inventory
     * @param {string} glyphId - Glyph item ID
     * @returns {number} Total quantity available
     */
    getTotalGlyphCount(glyphId) {
        let total = this.getItemCount(glyphId);

        if (this.isGlyphPouchUnlocked()) {
            const pouch = this.getGlyphPouch();
            for (const slot of pouch.slots) {
                if (slot.itemId === glyphId) {
                    total += slot.quantity;
                }
            }
        }

        return total;
    }

    // ==================== AUTO-EAT METHODS ====================
    //
    // FUTURE INTEGRATION: Architecting Milestone System
    // -------------------------------------------------
    // Current: Auto-eat unlocks at Combat Lv5
    // Future:  Combat Lv5 + Architecting Lv5 + "Cookfire" milestone built
    //
    // Integration Points:
    // 1. hasAutoEatMilestone() - Replace placeholder with real milestone check
    // 2. checkAutoEatUnlock() - Already calls hasAutoEatMilestone()
    // 3. combatUI.js renderAutoEatPanel() - Shows unlock requirements
    //
    // The dedicated food slot (storedFood) is already implemented:
    // - storeAutoEatFood(itemId, qty) - Store food (shift+click in inventory)
    // - consumeAutoEatFood() - Called by combat tick when HP threshold hit
    // -------------------------------------------------

    /**
     * Check if auto-eat milestone is built (Mess Hall from Architecting)
     * @returns {boolean} True if milestone requirement is met
     */
    hasAutoEatMilestone() {
        return this.hasMilestone('mess_hall');
    }

    /**
     * Get auto-eat settings
     * @returns {Object}
     */
    getAutoEat() {
        return this.getCombat().autoEat;
    }

    /**
     * Unlock auto-eat (called when reaching required combat level)
     */
    unlockAutoEat() {
        const autoEat = this.getAutoEat();
        autoEat.unlocked = true;
    }

    /**
     * Check if auto-eat should be unlocked based on requirements
     * Current: Combat Lv5
     * Future:  Combat Lv5 + Architecting Lv5 + milestone built
     * @returns {boolean} True if just unlocked
     */
    checkAutoEatUnlock() {
        const autoEat = this.getAutoEat();
        if (autoEat.unlocked) return false;

        const combatLevel = this.getSkill(SKILLS.COMBAT)?.level || 1;

        // Check combat level requirement
        if (combatLevel < COMBAT_CONFIG.AUTO_EAT_UNLOCK_LEVEL) return false;

        // Check milestone requirement (placeholder for future)
        if (!this.hasAutoEatMilestone()) return false;

        autoEat.unlocked = true;
        return true;
    }

    /**
     * Set auto-eat threshold (10-95)
     * @param {number} threshold - HP percentage threshold
     */
    setAutoEatThreshold(threshold) {
        const autoEat = this.getAutoEat();
        autoEat.threshold = Math.max(
            COMBAT_CONFIG.AUTO_EAT_MIN_THRESHOLD,
            Math.min(threshold, COMBAT_CONFIG.AUTO_EAT_MAX_THRESHOLD)
        );
    }

    /**
     * Store food for auto-eat
     * @param {string} itemId - Food item ID
     * @param {number} quantity - Quantity to store
     * @returns {{ success: boolean, reason?: string }}
     */
    storeAutoEatFood(itemId, quantity) {
        const autoEat = this.getAutoEat();

        // Check if auto-eat is unlocked
        if (!autoEat.unlocked) {
            this.checkAutoEatUnlock();
            if (!autoEat.unlocked) {
                return { success: false, reason: `Requires Combat Lv ${COMBAT_CONFIG.AUTO_EAT_UNLOCK_LEVEL} + Mess Hall` };
            }
        }

        // Check if we have the food
        const available = this.getItemCount(itemId);
        if (available < quantity) {
            return { success: false, reason: 'Not enough food' };
        }

        // Remove from inventory
        this.removeItem(itemId, quantity);

        // Add to stored (stack if same item)
        if (autoEat.storedFood && autoEat.storedFood.itemId === itemId) {
            autoEat.storedFood.quantity += quantity;
        } else {
            // If different food was stored, return it first
            if (autoEat.storedFood) {
                this.addItem(autoEat.storedFood.itemId, autoEat.storedFood.quantity);
            }
            autoEat.storedFood = { itemId, quantity };
        }

        return { success: true };
    }

    /**
     * Consume one stored food item
     * @returns {{ success: boolean, itemId?: string, reason?: string }}
     */
    consumeAutoEatFood() {
        const autoEat = this.getAutoEat();
        if (!autoEat.storedFood || autoEat.storedFood.quantity <= 0) {
            return { success: false, reason: 'No food stored' };
        }

        const itemId = autoEat.storedFood.itemId;
        autoEat.storedFood.quantity--;

        if (autoEat.storedFood.quantity <= 0) {
            autoEat.storedFood = null;
        }

        return { success: true, itemId };
    }

    /**
     * Return all stored auto-eat food back to inventory
     * @returns {{ success: boolean, itemId?: string, quantity?: number }}
     */
    returnAutoEatFood() {
        const autoEat = this.getAutoEat();
        if (!autoEat.storedFood || autoEat.storedFood.quantity <= 0) {
            return { success: false, reason: 'No food stored' };
        }

        const { itemId, quantity } = autoEat.storedFood;

        // Check if inventory can hold the items
        const addResult = this.addItem(itemId, quantity);
        if (!addResult.success) {
            return { success: false, reason: addResult.reason || 'Inventory full' };
        }

        autoEat.storedFood = null;

        return { success: true, itemId, quantity };
    }

    // ==================== POTION BUFF METHODS ====================

    /**
     * Get all active potion buffs
     * @returns {Object} Map of category -> buff data
     */
    getPotionBuffs() {
        return this.getCombat().potionBuffs;
    }

    /**
     * Get a specific potion buff by category
     * @param {string} category - Buff category (attack, defense, etc.)
     * @returns {Object|null} Buff data or null if not active
     */
    getPotionBuff(category) {
        return this.getCombat().potionBuffs[category] || null;
    }

    /**
     * Apply a potion buff (overwrites existing buff of same category)
     * @param {string} category - Buff category
     * @param {Object} buffData - Buff data (effects, combatTimeRemaining, potionId, startedAt)
     */
    setPotionBuff(category, buffData) {
        this.getCombat().potionBuffs[category] = buffData;
    }

    /**
     * Remove a potion buff by category
     * @param {string} category - Buff category to remove
     */
    removePotionBuff(category) {
        delete this.getCombat().potionBuffs[category];
    }

    /**
     * Clear all potion buffs (e.g., on death or Ascendant)
     */
    clearAllPotionBuffs() {
        this.getCombat().potionBuffs = {};
    }

    /**
     * Check if a potion buff category conflicts with Ascendant (all)
     * Ascendant Concoction has category 'all' and conflicts with everything
     * @param {string} category - Category to check
     * @returns {boolean} True if would conflict
     */
    hasPotionConflict(category) {
        const buffs = this.getPotionBuffs();
        // If trying to add 'all', conflicts with any existing buff
        if (category === 'all') {
            return Object.keys(buffs).length > 0;
        }
        // If 'all' is active, conflicts with any new buff
        if (buffs['all']) {
            return true;
        }
        // Same category conflicts
        return !!buffs[category];
    }

    /**
     * Get conflicting buff category names for UI display
     * @param {string} category - Category to check
     * @returns {string[]} Array of conflicting category names
     */
    getConflictingBuffs(category) {
        const buffs = this.getPotionBuffs();
        if (category === 'all') {
            return Object.keys(buffs);
        }
        if (buffs['all']) {
            return ['all'];
        }
        if (buffs[category]) {
            return [category];
        }
        return [];
    }

    // ==================== APOTHECARY BELT METHODS ====================

    /**
     * Get the Apothecary Belt state
     * @returns {{ unlocked: boolean, enabled: boolean, slots: Object }}
     */
    getApothecaryBelt() {
        return this.getCombat().apothecaryBelt;
    }

    /**
     * Get the active equipment set's belt slots
     * @returns {Array} Array of 4 slots for the current equipment set
     */
    getActiveBeltSlots() {
        const belt = this.getApothecaryBelt();
        const activeSet = this.player.activeEquipmentSet || 'melee';
        return belt.slots[activeSet];
    }

    /**
     * Check if Apothecary Station milestone is built
     * @returns {boolean}
     */
    hasApothecaryMilestone() {
        return this.hasMilestone('apothecary_station');
    }

    /**
     * Check if Apothecary Belt is unlocked
     * @returns {boolean}
     */
    isApothecaryBeltUnlocked() {
        const belt = this.getApothecaryBelt();
        // Auto-unlock if milestone is completed but belt not yet unlocked
        if (!belt.unlocked && this.hasApothecaryMilestone()) {
            belt.unlocked = true;
        }
        return belt.unlocked;
    }

    /**
     * Unlock the Apothecary Belt (called when milestone is completed)
     */
    unlockApothecaryBelt() {
        const belt = this.getApothecaryBelt();
        belt.unlocked = true;
    }

    /**
     * Toggle the Apothecary Belt on/off
     * @returns {boolean} New enabled state
     */
    toggleApothecaryBelt() {
        const belt = this.getApothecaryBelt();
        if (!belt.unlocked) return false;
        belt.enabled = !belt.enabled;
        return belt.enabled;
    }

    /**
     * Store potions in Apothecary Belt (4 slots per equipment set)
     * @param {number} slotIndex - Slot index (0-3)
     * @param {string} itemId - Potion item ID
     * @param {number} quantity - Amount to store
     * @returns {{ success: boolean, reason?: string }}
     */
    storeApothecaryPotion(slotIndex, itemId, quantity) {
        const belt = this.getApothecaryBelt();
        if (!belt.unlocked) {
            return { success: false, reason: 'Apothecary Belt not unlocked' };
        }

        if (slotIndex < 0 || slotIndex > 3) {
            return { success: false, reason: 'Invalid slot' };
        }

        // Get potion data to validate
        const potion = getItem(itemId);
        if (!potion || !potion.effects || !potion.effects.category) {
            return { success: false, reason: 'Not a valid potion' };
        }

        // Check if player has the potions
        const playerCount = this.getItemCount(itemId);
        if (playerCount < quantity) {
            return { success: false, reason: 'Not enough potions in inventory' };
        }

        // Remove from inventory
        this.removeItem(itemId, quantity);

        const slots = this.getActiveBeltSlots();

        // If there's a different potion in this slot, return it to inventory first
        if (slots[slotIndex] && slots[slotIndex].itemId !== itemId) {
            this.addItem(slots[slotIndex].itemId, slots[slotIndex].quantity);
            slots[slotIndex] = null;
        }

        // Add to belt slot (same potions stack)
        if (slots[slotIndex] && slots[slotIndex].itemId === itemId) {
            slots[slotIndex].quantity += quantity;
        } else {
            slots[slotIndex] = { itemId, quantity };
        }

        return { success: true };
    }

    /**
     * Remove potions from Apothecary Belt slot back to inventory
     * @param {number} slotIndex - Slot index (0-3)
     * @param {number} quantity - Amount to remove (0 = all)
     * @returns {{ success: boolean, reason?: string }}
     */
    removeApothecaryPotion(slotIndex, quantity = 0) {
        const slots = this.getActiveBeltSlots();
        if (slotIndex < 0 || slotIndex > 3 || !slots[slotIndex]) {
            return { success: false, reason: 'No potion in that slot' };
        }

        const slot = slots[slotIndex];
        const removeQty = quantity === 0 ? slot.quantity : Math.min(quantity, slot.quantity);

        // Check if inventory can hold the items
        const addResult = this.addItem(slot.itemId, removeQty);
        if (!addResult.success) {
            return { success: false, reason: addResult.reason || 'Inventory full' };
        }

        slot.quantity -= removeQty;

        if (slot.quantity <= 0) {
            slots[slotIndex] = null;
        }

        return { success: true };
    }

    /**
     * Consume one potion from Apothecary Belt by category (auto-potion)
     * Finds a potion matching the category in any slot
     * @param {string} category - Buff category to consume
     * @returns {{ success: boolean, itemId?: string, slotIndex?: number, reason?: string }}
     */
    consumeApothecaryPotion(category) {
        const slots = this.getActiveBeltSlots();

        // Find a slot with a potion of this category
        let slotIndex = -1;
        for (let i = 0; i < slots.length; i++) {
            const slot = slots[i];
            if (slot && slot.quantity > 0) {
                const potion = getItem(slot.itemId);
                if (potion && potion.effects && potion.effects.category === category) {
                    slotIndex = i;
                    break;
                }
            }
        }

        if (slotIndex === -1) {
            return { success: false, reason: 'No potion stored for this category' };
        }

        const slot = slots[slotIndex];
        const itemId = slot.itemId;
        slot.quantity--;

        if (slot.quantity <= 0) {
            slots[slotIndex] = null;
        }

        return { success: true, itemId, slotIndex };
    }

    /**
     * Get a belt slot by index
     * @param {number} slotIndex - Slot index (0-3)
     * @returns {{ itemId: string, quantity: number }|null}
     */
    getApothecarySlot(slotIndex) {
        const slots = this.getActiveBeltSlots();
        return slots[slotIndex] || null;
    }

    /**
     * Find a belt slot containing a potion of the given category
     * @param {string} category - Buff category
     * @returns {{ slotIndex: number, slot: { itemId: string, quantity: number }}|null}
     */
    findApothecarySlotByCategory(category) {
        const slots = this.getActiveBeltSlots();
        for (let i = 0; i < slots.length; i++) {
            const slot = slots[i];
            if (slot && slot.quantity > 0) {
                const potion = getItem(slot.itemId);
                if (potion && potion.effects && potion.effects.category === category) {
                    return { slotIndex: i, slot };
                }
            }
        }
        return null;
    }

    /**
     * Get active set's belt slots
     * @returns {Array} Array of { itemId, quantity } or null
     */
    getApothecarySlots() {
        return this.getActiveBeltSlots();
    }

    // ==================== TOOL PURCHASE/EQUIP METHODS ====================

    /**
     * Check if a tool has been purchased for a skill
     * @param {string} skillId - Skill identifier
     * @param {string} toolId - Tool identifier
     * @returns {boolean}
     */
    hasPurchasedTool(skillId, toolId) {
        const purchased = this.player.purchasedTools[skillId] || [];
        return purchased.includes(toolId);
    }

    /**
     * Get all purchased tools for a skill
     * @param {string} skillId - Skill identifier
     * @returns {string[]} Array of tool IDs
     */
    getPurchasedTools(skillId) {
        return this.player.purchasedTools[skillId] || [];
    }

    /**
     * Check if a skill is currently corrupted (gold costs waived)
     * @param {string} skillId - Skill identifier
     * @returns {boolean}
     */
    isSkillCorrupted(skillId) {
        const corruption = this.getCorruption();
        return corruption.skills?.[skillId]?.active === true;
    }

    /**
     * Purchase a tool (deducts gold, marks as owned)
     * Note: Tool purchases ALWAYS cost gold, even during corruption
     * @param {string} skillId - Skill identifier
     * @param {string} toolId - Tool identifier
     * @param {number} price - Gold cost
     * @returns {{ success: boolean, reason?: string }}
     */
    purchaseTool(skillId, toolId, price) {
        // Check if already purchased
        if (this.hasPurchasedTool(skillId, toolId)) {
            return { success: false, reason: 'Already owned' };
        }

        // Check gold (coins) - tool purchases always cost gold
        const goldCount = this.getItemCount('coins');
        if (goldCount < price) {
            return { success: false, reason: `Need ${price} gold (have ${goldCount})` };
        }

        // Deduct gold
        if (price > 0) {
            this.removeItem('coins', price);
        }

        // Add to purchased list
        if (!this.player.purchasedTools[skillId]) {
            this.player.purchasedTools[skillId] = [];
        }
        this.player.purchasedTools[skillId].push(toolId);

        return { success: true };
    }

    /**
     * Get the equipped tool ID for a skill
     * @param {string} skillId - Skill identifier
     * @returns {string|null} Tool ID or null
     */
    getEquippedTool(skillId) {
        return this.player.equippedTools[skillId] || null;
    }

    /**
     * Set the equipped tool for a skill
     * @param {string} skillId - Skill identifier
     * @param {string} toolId - Tool identifier
     */
    equipTool(skillId, toolId) {
        this.player.equippedTools[skillId] = toolId;
    }

    /**
     * Get the highest tier purchased tool from a tools array
     * Tools must be ordered by tier (index 0 = lowest tier)
     * @param {string} skillId - Skill identifier
     * @param {Array} toolsArray - Array of tool objects with 'id' property, ordered by tier
     * @returns {object|null} Highest tier purchased tool or null
     */
    getHighestPurchasedTool(skillId, toolsArray) {
        const purchased = this.getPurchasedTools(skillId);
        if (purchased.length === 0) return null;

        // Find highest tier tool that's owned (iterate backwards since array is ordered low to high)
        for (let i = toolsArray.length - 1; i >= 0; i--) {
            if (purchased.includes(toolsArray[i].id)) {
                return toolsArray[i];
            }
        }
        return null;
    }

    /**
     * Reset purchased tools (for admin/testing)
     * @param {string} [skillId] - Skill to reset, or all if not provided
     */
    resetPurchasedTools(skillId) {
        if (skillId) {
            this.player.purchasedTools[skillId] = [];
            delete this.player.equippedTools[skillId];
        } else {
            this.player.purchasedTools = {};
            this.player.equippedTools = {};
        }
    }

    // ==================== QUESTS ====================

    /**
     * Get quest state
     */
    getQuestState() {
        return this.player.quests;
    }

    /**
     * Get completed quests array
     */
    getCompletedQuests() {
        return this.player.quests.completed || [];
    }

    /**
     * Get count of completed quests
     */
    getCompletedQuestCount() {
        return this.getCompletedQuests().length;
    }

    /**
     * Check if a specific quest is completed
     */
    isQuestCompleted(questNumber) {
        return this.getCompletedQuests().includes(questNumber);
    }

    /**
     * Get active quest (if any)
     */
    getActiveQuest() {
        return this.player.quests.active;
    }

    /**
     * Check if player has an active quest
     */
    hasActiveQuest() {
        return this.player.quests.active !== null;
    }

    /**
     * Get quest progress (0-1) for active quest
     */
    getQuestProgress() {
        const active = this.player.quests.active;
        if (!active) return 0;

        const quest = getQuest(active.questNumber);
        if (!quest) return 0;

        const elapsed = Date.now() - active.startedAt;
        return Math.min(elapsed / quest.duration, 1);
    }

    /**
     * Check if active quest is complete (timer finished)
     */
    isActiveQuestComplete() {
        return this.getQuestProgress() >= 1;
    }

    /**
     * Get quest unlocks
     */
    getQuestUnlocks() {
        return this.player.quests.unlocks || [];
    }

    // =========================================================================
    // PINNED QUESTS
    // =========================================================================

    /**
     * Get all pinned quest numbers
     * @returns {number[]}
     */
    getPinnedQuests() {
        return this.player.quests.pinned || [];
    }

    /**
     * Check if a quest is pinned
     * @param {number} questNumber
     * @returns {boolean}
     */
    isQuestPinned(questNumber) {
        return this.getPinnedQuests().includes(questNumber);
    }

    /**
     * Pin a quest for tracking (max 3)
     * @param {number} questNumber
     * @returns {{ success: boolean, reason?: string }}
     */
    pinQuest(questNumber) {
        if (!this.player.quests.pinned) {
            this.player.quests.pinned = [];
        }

        // Already pinned
        if (this.isQuestPinned(questNumber)) {
            return { success: false, reason: 'Quest already pinned' };
        }

        // Max 3 pinned
        if (this.player.quests.pinned.length >= 3) {
            return { success: false, reason: 'Maximum 3 quests can be pinned' };
        }

        // Can't pin completed quests
        if (this.isQuestCompleted(questNumber)) {
            return { success: false, reason: 'Cannot pin completed quest' };
        }

        // Can't pin active quest
        const activeQuest = this.getActiveQuest();
        if (activeQuest && activeQuest.questNumber === questNumber) {
            return { success: false, reason: 'Cannot pin active quest' };
        }

        this.player.quests.pinned.push(questNumber);
        return { success: true };
    }

    /**
     * Unpin a quest
     * @param {number} questNumber
     * @returns {{ success: boolean }}
     */
    unpinQuest(questNumber) {
        if (!this.player.quests.pinned) {
            return { success: false };
        }

        const idx = this.player.quests.pinned.indexOf(questNumber);
        if (idx === -1) {
            return { success: false };
        }

        this.player.quests.pinned.splice(idx, 1);
        return { success: true };
    }

    /**
     * Check requirements for starting a quest
     * @returns {{ canStart: boolean, missing: string[] }}
     */
    canStartQuest(questNumber) {
        const quest = getQuest(questNumber);
        if (!quest) return { canStart: false, missing: ['Quest not found'] };

        // Already completed
        if (this.isQuestCompleted(questNumber)) {
            return { canStart: false, missing: ['Quest already completed'] };
        }

        const missing = [];
        const req = quest.requirements;

        // Check skill requirements
        if (req.skills) {
            for (const [skillId, level] of Object.entries(req.skills)) {
                const playerLevel = this.getSkill(skillId)?.level || 0;
                if (playerLevel < level) {
                    const skillName = skillId.charAt(0).toUpperCase() + skillId.slice(1);
                    missing.push(`${skillName} level ${level} (have ${playerLevel})`);
                }
            }
        }

        // Check gold requirement
        if (req.gold) {
            const playerGold = this.getGold();
            if (playerGold < req.gold) {
                missing.push(`${req.gold.toLocaleString()} gold (have ${playerGold.toLocaleString()})`);
            }
        }

        // Check item requirements
        if (req.items) {
            for (const [itemId, qty] of Object.entries(req.items)) {
                const playerQty = this.getItemCount(itemId);
                if (playerQty < qty) {
                    const itemName = getItem(itemId)?.name || itemId;
                    missing.push(`${itemName} x${qty} (have ${playerQty})`);
                }
            }
        }

        // Check prerequisite quests
        if (req.quests) {
            for (const prereqNum of req.quests) {
                if (!this.isQuestCompleted(prereqNum)) {
                    const prereq = getQuest(prereqNum);
                    missing.push(`Complete quest: ${prereq?.name || `Quest #${prereqNum}`}`);
                }
            }
        }

        // Check minimum total quests completed
        if (req.questCount) {
            const completed = this.getCompletedQuestCount();
            if (completed < req.questCount) {
                missing.push(`Complete ${req.questCount} quests (have ${completed})`);
            }
        }

        // Check milestone requirements
        if (req.milestones) {
            for (const milestoneId of req.milestones) {
                if (!this.isMilestoneCompleted(milestoneId)) {
                    missing.push(`Build milestone: ${milestoneId}`);
                }
            }
        }

        // Check pet requirements
        if (req.pets) {
            for (const petId of Object.keys(req.pets)) {
                const pet = this.player.pets.find(p => p.id === petId);
                if (!pet || !pet.unlocked) {
                    missing.push(`Unlock pet: ${petId}`);
                }
            }
        }

        // Check pet evolution requirements
        if (req.petEvolutions) {
            for (const [petId, evolutionLevel] of Object.entries(req.petEvolutions)) {
                const pet = this.player.pets.find(p => p.id === petId);
                if (!pet || pet.evolution < evolutionLevel) {
                    const current = pet?.evolution || 0;
                    missing.push(`Evolve ${petId} to level ${evolutionLevel} (at ${current})`);
                }
            }
        }

        // Check unlock requirements (must have these unlocks from previous quests)
        if (req.unlocks) {
            for (const unlockId of req.unlocks) {
                if (!this.hasQuestUnlock(unlockId)) {
                    const unlockName = this.getUnlockDisplayName(unlockId);
                    missing.push(`Unlock: ${unlockName}`);
                }
            }
        }

        // Check boss defeat requirements (must have defeated specific bosses)
        if (req.bossDefeats) {
            for (const bossId of req.bossDefeats) {
                if (!this.hasDefeatedBoss(bossId)) {
                    // Format boss name from ID (e.g. 'ashen_warden' → 'Ashen Warden')
                    const bossName = bossId.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    missing.push(`Defeat: ${bossName}`);
                }
            }
        }

        return { canStart: missing.length === 0, missing };
    }

    /**
     * Check if player has a specific quest unlock
     */
    hasQuestUnlock(unlockId) {
        return this.player.quests?.unlocks?.includes(unlockId) || false;
    }

    /**
     * Get display name for an unlock (for requirement display)
     */
    getUnlockDisplayName(unlockId) {
        const unlockNames = {
            'glyph_pouch': 'Glyph Pouch',
            'quiver_leather': 'Leather Quiver',
            'quiver_ironwood': 'Ironwood Quiver',
            'quiver_ashen': 'Ashen Quiver',
            'quiver_veilstalker': 'Veilstalker Quiver',
            'pet_eidolon': 'Eidolon Pet',
            'zone_ashen_crucible_t1': 'Ashen Crucible — Dungeon Bosses',
            'zone_ashen_crucible_t2': 'Ashen Crucible — Set Bosses',
            'zone_ashen_crucible_t3': 'Ashen Crucible — The Convergence',
            'contractor_hired': 'Contractor'
        };
        return unlockNames[unlockId] || unlockId;
    }

    /**
     * Start a quest (consumes requirements)
     * @returns {{ success: boolean, reason?: string }}
     */
    startQuest(questNumber) {
        const quest = getQuest(questNumber);
        if (!quest) return { success: false, reason: 'Quest not found' };

        const { canStart, missing } = this.canStartQuest(questNumber);
        if (!canStart) {
            return { success: false, reason: `Missing: ${missing.join(', ')}` };
        }

        const req = quest.requirements;

        // Consume gold
        if (req.gold) {
            this.removeGold(req.gold);
        }

        // Consume items
        if (req.items) {
            for (const [itemId, qty] of Object.entries(req.items)) {
                this.removeItem(itemId, qty);
            }
        }

        // Auto-unpin quest when started
        this.unpinQuest(questNumber);

        // Set active quest
        this.player.quests.active = {
            questNumber,
            startedAt: Date.now()
        };

        return { success: true };
    }

    /**
     * Cancel active quest (does not refund consumed items)
     */
    cancelActiveQuest() {
        this.player.quests.active = null;
    }

    /**
     * Check if quest rewards can fit in inventory
     * @returns {{ canFit: boolean, missing: string[] }}
     */
    canFitQuestRewards(questNumber) {
        const quest = getQuest(questNumber);
        if (!quest || !quest.rewards?.items) return { canFit: true, missing: [] };

        const missing = [];

        // Track used inventory slots for this calculation
        const usedSlotIds = new Set();

        // Check each reward item
        for (const [itemId, qty] of Object.entries(quest.rewards.items)) {
            // Check if item can stack on existing slot or if we have empty slots
            const existingSlot = this.player.inventorySlots.find(
                slot => slot && slot.itemId === itemId
            );

            if (!existingSlot) {
                // Need an empty slot - find one not already claimed
                const emptySlotIdx = this.player.inventorySlots.findIndex(
                    (slot, idx) => slot === null && !usedSlotIds.has(idx)
                );
                if (emptySlotIdx === -1) {
                    const itemName = getItem(itemId)?.name || itemId;
                    missing.push(itemName);
                } else {
                    usedSlotIds.add(emptySlotIdx);
                }
            }
            // If item exists, it can always stack (no max stack for inventory)
        }

        return { canFit: missing.length === 0, missing };
    }

    /**
     * Complete active quest and claim rewards
     * @returns {{ success: boolean, reason?: string, rewards?: object }}
     */
    completeQuest() {
        const active = this.player.quests.active;
        if (!active) return { success: false, reason: 'No active quest' };

        // Check if timer is done
        if (!this.isActiveQuestComplete()) {
            return { success: false, reason: 'Quest not yet complete' };
        }

        const quest = getQuest(active.questNumber);
        if (!quest) return { success: false, reason: 'Quest not found' };

        // Check if rewards fit
        const { canFit, missing } = this.canFitQuestRewards(active.questNumber);
        if (!canFit) {
            return { success: false, reason: `Inventory full! Free up space for: ${missing.join(', ')}` };
        }

        // Award item rewards
        const awardedItems = {};
        if (quest.rewards?.items) {
            for (const [itemId, qty] of Object.entries(quest.rewards.items)) {
                this.addItem(itemId, qty);
                awardedItems[itemId] = qty;
            }
        }

        // Process unlocks
        if (quest.rewards?.unlocks) {
            for (const unlockId of quest.rewards.unlocks) {
                if (!this.hasQuestUnlock(unlockId)) {
                    this.player.quests.unlocks.push(unlockId);
                }

                // Handle special unlocks
                if (unlockId === 'pet_eidolon') {
                    const eidolon = this.player.pets.find(p => p.id === 'eidolon');
                    if (eidolon) eidolon.unlocked = true;
                }
                if (unlockId === 'glyph_pouch') {
                    this.getGlyphPouch().unlocked = true;
                }
            }
        }

        // Mark quest complete
        this.player.quests.completed.push(active.questNumber);
        this.player.quests.active = null;

        return {
            success: true,
            rewards: {
                items: awardedItems,
                unlocks: quest.rewards?.unlocks || [],
                xpRewards: quest.rewards?.xpRewards || {}
            }
        };
    }

    /**
     * Force complete a quest (admin/testing only)
     * Skips timer check, awards rewards, processes unlocks
     * @param {number} questNumber - Quest number to complete
     * @returns {{ success: boolean, reason?: string }}
     */
    forceCompleteQuest(questNumber) {
        if (this.isQuestCompleted(questNumber)) {
            return { success: false, reason: 'Quest already completed' };
        }

        const quest = getQuest(questNumber);
        if (!quest) {
            return { success: false, reason: 'Quest not found' };
        }

        // Award item rewards
        if (quest.rewards?.items) {
            for (const [itemId, qty] of Object.entries(quest.rewards.items)) {
                this.addItem(itemId, qty);
            }
        }

        // Process unlocks
        if (quest.rewards?.unlocks) {
            for (const unlockId of quest.rewards.unlocks) {
                if (!this.hasQuestUnlock(unlockId)) {
                    this.player.quests.unlocks.push(unlockId);
                }

                // Handle special unlocks
                if (unlockId === 'pet_eidolon') {
                    const eidolon = this.player.pets.find(p => p.id === 'eidolon');
                    if (eidolon) eidolon.unlocked = true;
                }
                if (unlockId === 'glyph_pouch') {
                    this.getGlyphPouch().unlocked = true;
                }
            }
        }

        // Mark quest complete
        this.player.quests.completed.push(questNumber);

        // Clear active quest if it matches
        if (this.player.quests.active?.questNumber === questNumber) {
            this.player.quests.active = null;
        }

        return { success: true };
    }

    /**
     * Get available quests (not completed, requirements met)
     */
    getAvailableQuests() {
        const available = [];
        const totalQuests = getTotalQuestCount();

        for (let i = 1; i <= totalQuests; i++) {
            if (!this.isQuestCompleted(i)) {
                const { canStart } = this.canStartQuest(i);
                if (canStart) {
                    available.push(i);
                }
            }
        }

        return available;
    }

    /**
     * Check if a milestone is completed (for quest requirements)
     * Delegates to architecting state
     */
    isMilestoneCompleted(milestoneId) {
        return this.player.architecting?.completedMilestones?.includes(milestoneId) || false;
    }

    // ==================== IMPORT/EXPORT ====================

    /**
     * Migrate old object-based inventory to new slot-based format
     * @param {object} oldInventory - { itemId: quantity }
     * @returns {Array} New slot-based inventory
     */
    migrateInventory(oldInventory) {
        const slots = new Array(INVENTORY_SLOTS).fill(null);
        const entries = Object.entries(oldInventory);

        for (let i = 0; i < Math.min(entries.length, INVENTORY_SLOTS); i++) {
            const [itemId, quantity] = entries[i];
            slots[i] = { itemId, quantity };
        }

        return slots;
    }

    /**
     * Migrate old object-based bank to new slot-based format
     * @param {object} oldBank - { itemId: quantity }
     * @returns {Array} New slot-based bank
     */
    migrateBank(oldBank) {
        return Object.entries(oldBank).map(([itemId, quantity]) => ({
            itemId,
            quantity
        }));
    }

    /**
     * Remove invalid/orphaned items from all storage locations
     * Called during import to clean up items from old saves that no longer exist
     */
    cleanupInvalidItems() {
        const removedItems = [];

        // Helper to check if item exists in registry
        const isValidItem = (itemId) => {
            if (!itemId) return false;
            return getItem(itemId) !== null && getItem(itemId) !== undefined;
        };

        // Clean inventory slots (array of { itemId, quantity } or null)
        for (let i = 0; i < this.player.inventorySlots.length; i++) {
            const slot = this.player.inventorySlots[i];
            if (slot && slot.itemId && !isValidItem(slot.itemId)) {
                removedItems.push({ location: 'inventory', itemId: slot.itemId, quantity: slot.quantity });
                this.player.inventorySlots[i] = null;
            }
        }

        // Clean bank slots (array of { itemId, quantity })
        this.player.bankSlots = this.player.bankSlots.filter(slot => {
            if (slot && slot.itemId && !isValidItem(slot.itemId)) {
                removedItems.push({ location: 'bank', itemId: slot.itemId, quantity: slot.quantity });
                return false;
            }
            return true;
        });

        // Clean bloomhold slots (array of { itemId, quantity })
        this.player.bloomholdSlots = this.player.bloomholdSlots.filter(slot => {
            if (slot && slot.itemId && !isValidItem(slot.itemId)) {
                removedItems.push({ location: 'bloomhold', itemId: slot.itemId, quantity: slot.quantity });
                return false;
            }
            return true;
        });

        // Clean loot chest - pet loot (array of { itemId, quantity })
        this.player.lootChest = this.player.lootChest.filter(slot => {
            if (slot && slot.itemId && !isValidItem(slot.itemId)) {
                removedItems.push({ location: 'lootChest', itemId: slot.itemId, quantity: slot.quantity });
                return false;
            }
            return true;
        });

        // Clean pilfering guild chest (array of { itemId, quantity })
        if (this.player.pilferingGuild?.chest) {
            this.player.pilferingGuild.chest = this.player.pilferingGuild.chest.filter(slot => {
                if (slot && slot.itemId && !isValidItem(slot.itemId)) {
                    removedItems.push({ location: 'guildChest', itemId: slot.itemId, quantity: slot.quantity });
                    return false;
                }
                return true;
            });
        }

        // Clean combat loot chest (object { itemId: quantity })
        if (this.player.combat?.lootChest) {
            for (const itemId of Object.keys(this.player.combat.lootChest)) {
                if (!isValidItem(itemId)) {
                    removedItems.push({ location: 'combatLoot', itemId, quantity: this.player.combat.lootChest[itemId] });
                    delete this.player.combat.lootChest[itemId];
                }
            }
        }

        // Clean locked items array (just itemIds)
        if (this.player.lockedItems?.length > 0) {
            this.player.lockedItems = this.player.lockedItems.filter(itemId => {
                if (!isValidItem(itemId)) {
                    removedItems.push({ location: 'lockedItems', itemId, quantity: 0 });
                    return false;
                }
                return true;
            });
        }

        // Log cleanup results
        if (removedItems.length > 0) {
            console.warn(`[SAVE CLEANUP] Removed ${removedItems.length} invalid/orphaned items from save:`);
            for (const item of removedItems) {
                console.warn(`  - ${item.itemId} x${item.quantity} from ${item.location}`);
            }
        }

        return removedItems;
    }

    import(data) {
        const fresh = new GameState();

        if (data.player) {
            if (data.player.skills) {
                for (const skillId of Object.values(SKILLS)) {
                    if (data.player.skills[skillId]) {
                        Object.assign(
                            fresh.player.skills[skillId],
                            data.player.skills[skillId]
                        );
                    }
                }
            }

            // Handle inventory - support both old object format and new slot format
            if (data.player.inventorySlots && Array.isArray(data.player.inventorySlots)) {
                // New format - use directly
                fresh.player.inventorySlots = data.player.inventorySlots;
            } else if (data.player.inventory && typeof data.player.inventory === 'object') {
                // Old format - migrate
                fresh.player.inventorySlots = this.migrateInventory(data.player.inventory);
            }

            // Handle bank - support both old object format and new slot format
            if (data.player.bankSlots && Array.isArray(data.player.bankSlots)) {
                // New format - use directly
                fresh.player.bankSlots = data.player.bankSlots;
            } else if (data.player.bank && typeof data.player.bank === 'object') {
                // Old format - migrate
                fresh.player.bankSlots = this.migrateBank(data.player.bank);
            }

            // Handle farm plots - use saved data or initialize fresh
            if (data.player.farmPlots && Array.isArray(data.player.farmPlots)) {
                fresh.player.farmPlots = data.player.farmPlots;
            }
            // farmPlots already initialized in constructor if not in save

            // Handle farm settings
            if (data.player.farmSettings) {
                Object.assign(fresh.player.farmSettings, data.player.farmSettings);
            }

            // Handle purchased tools
            if (data.player.purchasedTools) {
                fresh.player.purchasedTools = data.player.purchasedTools;
            }

            // Handle equipped tools
            if (data.player.equippedTools) {
                fresh.player.equippedTools = data.player.equippedTools;
            }

            // Handle locked items
            if (data.player.lockedItems && Array.isArray(data.player.lockedItems)) {
                fresh.player.lockedItems = data.player.lockedItems;
            }

            // Handle BloomHold
            if (data.player.bloomholdSlots && Array.isArray(data.player.bloomholdSlots)) {
                fresh.player.bloomholdSlots = data.player.bloomholdSlots;
            }
            if (data.player.bloomholdUnlocked !== undefined) {
                fresh.player.bloomholdUnlocked = data.player.bloomholdUnlocked;
            }
            if (data.player.bloomholdAutoDeposit !== undefined) {
                fresh.player.bloomholdAutoDeposit = data.player.bloomholdAutoDeposit;
            }

            // Handle Loot Chest
            if (data.player.lootChest && Array.isArray(data.player.lootChest)) {
                fresh.player.lootChest = data.player.lootChest;
            }

            // Migrate renamed items (ball_of_wool → spun_fiber)
            const ITEM_RENAMES = { 'ball_of_wool': 'spun_fiber', 'kingsroot': 'kings_fruit', 'kingsroot_seed': 'kings_fruit_seed' };
            const storagesToMigrate = [
                fresh.player.inventorySlots,
                fresh.player.bankSlots,
                fresh.player.bloomholdSlots,
                fresh.player.lootChest
            ];
            for (const storage of storagesToMigrate) {
                if (!Array.isArray(storage)) continue;
                for (const slot of storage) {
                    if (slot && slot.itemId && ITEM_RENAMES[slot.itemId]) {
                        slot.itemId = ITEM_RENAMES[slot.itemId];
                    }
                }
            }
            // Also migrate auto-eat stored food
            if (fresh.player.autoEat && fresh.player.autoEat.storedFood) {
                const sf = fresh.player.autoEat.storedFood;
                if (sf.itemId && ITEM_RENAMES[sf.itemId]) {
                    sf.itemId = ITEM_RENAMES[sf.itemId];
                }
            }
            // Migrate locked item IDs
            if (Array.isArray(fresh.player.lockedItems)) {
                fresh.player.lockedItems = fresh.player.lockedItems.map(id => ITEM_RENAMES[id] || id);
            }

            // Handle Pets
            if (data.player.pets && Array.isArray(data.player.pets)) {
                // Merge saved pet data with default pets (in case new pets were added)
                for (const savedPet of data.player.pets) {
                    const freshPet = fresh.player.pets.find(p => p.id === savedPet.id);
                    if (freshPet) {
                        Object.assign(freshPet, savedPet);
                    }
                }
            }

            // Handle Completed Contract Types
            if (data.player.completedContractTypes && Array.isArray(data.player.completedContractTypes)) {
                fresh.player.completedContractTypes = data.player.completedContractTypes;
            }

            // Handle Active Contracts
            if (data.player.activeContracts && Array.isArray(data.player.activeContracts)) {
                fresh.player.activeContracts = data.player.activeContracts;
            }

            // Handle Gatherer Zone
            if (data.player.gathererZone !== undefined) {
                fresh.player.gathererZone = data.player.gathererZone;
            }

            // Handle Bounty Contracts
            if (data.player.bountyContracts && Array.isArray(data.player.bountyContracts)) {
                fresh.player.bountyContracts = data.player.bountyContracts;
            }

            // Handle Bounty tracking data
            if (data.player.bountyStreak !== undefined) {
                fresh.player.bountyStreak = data.player.bountyStreak;
            }
            if (data.player.bountyTypeCompletions) {
                fresh.player.bountyTypeCompletions = data.player.bountyTypeCompletions;
            }
            if (data.player.bountyAbandonCount !== undefined) {
                fresh.player.bountyAbandonCount = data.player.bountyAbandonCount;
            }

            if (data.player.currentAction) {
                fresh.player.currentAction = data.player.currentAction;
            }

            if (data.player.stats) {
                Object.assign(fresh.player.stats, data.player.stats);
            }

            // Handle Tome of Echoes
            if (data.player.tomeOfEchoes && data.player.tomeOfEchoes.itemCounts) {
                fresh.player.tomeOfEchoes = data.player.tomeOfEchoes;
            } else {
                // Retroactive initialization for existing saves - scan all storage locations
                // and mark items as discovered with their current quantities
                const tomeItemCounts = {};

                // Scan inventory
                for (const slot of fresh.player.inventorySlots) {
                    if (slot && slot.itemId) {
                        tomeItemCounts[slot.itemId] = (tomeItemCounts[slot.itemId] || 0) + slot.quantity;
                    }
                }

                // Scan bank
                for (const slot of fresh.player.bankSlots) {
                    if (slot && slot.itemId) {
                        tomeItemCounts[slot.itemId] = (tomeItemCounts[slot.itemId] || 0) + slot.quantity;
                    }
                }

                // Scan BloomHold
                for (const slot of fresh.player.bloomholdSlots) {
                    if (slot && slot.itemId) {
                        tomeItemCounts[slot.itemId] = (tomeItemCounts[slot.itemId] || 0) + slot.quantity;
                    }
                }

                // Scan Loot Chest
                for (const slot of fresh.player.lootChest) {
                    if (slot && slot.itemId) {
                        tomeItemCounts[slot.itemId] = (tomeItemCounts[slot.itemId] || 0) + slot.quantity;
                    }
                }

                fresh.player.tomeOfEchoes = { itemCounts: tomeItemCounts };
            }

            // Prune ghost items from the Tome of Echoes: counts for items removed
            // from the registry (deleted or renamed). Leaving them inflates the
            // discovery count and pushes completion past 100%. Runs unconditionally
            // for BOTH branches above, so every load path is cleaned: normal load,
            // imported save files, and retroactively-scanned legacy saves. The next
            // save write then persists the cleaned data. Idempotent and self-healing
            // for any future item removals — no version bump needed.
            const tomeCounts = fresh.player.tomeOfEchoes.itemCounts;
            let prunedGhosts = 0;
            for (const id of Object.keys(tomeCounts)) {
                if (!getItem(id)) {
                    delete tomeCounts[id];
                    prunedGhosts++;
                }
            }
            if (prunedGhosts > 0) {
                console.log(`[MIGRATION] Pruned ${prunedGhosts} ghost item(s) from Tome of Echoes`);
            }

            // Handle Talent Trees - MUST be loaded before combat HP migration
            // so that talent HP bonuses are included in maxHp calculation
            if (data.player.talentTrees) {
                fresh.player.talentTrees = data.player.talentTrees;
            }

            // Handle Combat state
            if (data.player.combat) {
                // Merge with defaults to ensure all properties exist
                Object.assign(fresh.player.combat, data.player.combat);
                // Ensure autoEat exists with defaults
                if (!fresh.player.combat.autoEat) {
                    fresh.player.combat.autoEat = {
                        unlocked: false,
                        enabled: false,
                        storedFood: null,
                        threshold: 50
                    };
                }
                // HP Migration: Clamp current HP to new level-based max
                // This handles existing saves where HP was always 99
                const combatLevel = Math.min(fresh.player.skills[SKILLS.COMBAT]?.level || 1, 99);
                const hpRange = COMBAT_CONFIG.MAX_HP - COMBAT_CONFIG.MIN_HP;
                const baseMaxHp = Math.floor(COMBAT_CONFIG.MIN_HP + (combatLevel - 1) * (hpRange / 98));

                // Include talent bonus (Vital Force: +5 HP per rank)
                const vitalForceRank = fresh.player.talentTrees?.combat?.nodes?.combat_vital || 0;
                const talentHpBonus = vitalForceRank * 5;
                const maxHp = baseMaxHp + talentHpBonus;

                if (fresh.player.combat.currentHp > maxHp) {
                    fresh.player.combat.currentHp = maxHp;
                }

                // Always clear death state on load — no reason to persist death through reload.
                // The respawn timer is for live gameplay only.
                if (fresh.player.combat.isDead) {
                    fresh.player.combat.isDead = false;
                    fresh.player.combat.deathTime = null;
                    fresh.player.combat.currentHp = maxHp;
                    fresh.player.combat.inCombat = false;
                    fresh.player.combat.currentMob = null;
                    fresh.player.combat.mobCurrentHp = 0;
                }

                // Migrate old zone progress to Ashen Crucible
                const zp = fresh.player.combat.zoneProgress;
                if (zp) {
                    if (zp['cindered_reliquary'] || zp['ashen_convergence']) {
                        const oldCR = zp['cindered_reliquary'] || {};
                        const oldAC = zp['ashen_convergence'] || {};
                        if (!zp['ashen_crucible']) {
                            zp['ashen_crucible'] = {
                                cleared: oldCR.cleared || oldAC.cleared || false,
                                killsAtClear: Math.max(oldCR.killsAtClear || 0, oldAC.killsAtClear || 0),
                                clearedAt: oldCR.clearedAt || oldAC.clearedAt || null,
                                lifetimeKills: (oldCR.lifetimeKills || 0) + (oldAC.lifetimeKills || 0)
                            };
                        }
                        delete zp['cindered_reliquary'];
                        delete zp['ashen_convergence'];
                    }
                }

                // Migrate currentZone if player was fighting in old zone
                const oldZone = fresh.player.combat.currentZone;
                if (oldZone === 'cindered_reliquary' || oldZone === 'ashen_convergence') {
                    fresh.player.combat.currentZone = 'ashen_crucible';
                    fresh.player.combat.currentTier = oldZone === 'ashen_convergence' ? 't3' : 't1';
                }
            }

            // Handle Equipment Sets
            if (data.player.equipmentSets) {
                for (const setId of ['melee', 'ranged', 'magic']) {
                    if (data.player.equipmentSets[setId]) {
                        Object.assign(fresh.player.equipmentSets[setId], data.player.equipmentSets[setId]);
                    }
                }
            }

            // Migrate old equippedGear into melee set (for old saves)
            // Only migrate if we have old equippedGear but sets are empty
            if (data.player.equippedGear && !data.player.equipmentSets) {
                Object.assign(fresh.player.equipmentSets.melee.gear, data.player.equippedGear);
            }

            // Set active equipment set (default to melee)
            if (data.player.activeEquipmentSet) {
                fresh.player.activeEquipmentSet = data.player.activeEquipmentSet;
            } else {
                fresh.player.activeEquipmentSet = 'melee';
            }

            // Handle Pilfering Guild
            if (data.player.pilferingGuild) {
                // Merge saved guild state with defaults
                Object.assign(fresh.player.pilferingGuild, data.player.pilferingGuild);

                // Ensure pilferers array has all 7 domains (in case new ones were added)
                const defaultDomains = ['mining', 'smithing', 'textiles', 'hunting', 'foods', 'seeds', 'glyphs'];
                for (const domainId of defaultDomains) {
                    const exists = fresh.player.pilferingGuild.pilferers.find(p => p.domainId === domainId);
                    if (!exists) {
                        fresh.player.pilferingGuild.pilferers.push({
                            domainId,
                            active: false,
                            startedAt: null,
                            lastItemId: null
                        });
                    }
                }
            }

            // Handle Architecting (milestones, civic contracts)
            if (data.player.architecting) {
                Object.assign(fresh.player.architecting, data.player.architecting);
            }

            // Handle Quests
            if (data.player.quests) {
                fresh.player.quests = {
                    completed: data.player.quests.completed || [],
                    active: data.player.quests.active || null,
                    unlocks: data.player.quests.unlocks || [],
                    pinned: data.player.quests.pinned || []
                };

                // Migrate old zone unlocks to new Ashen Crucible tiers
                const unlocks = fresh.player.quests.unlocks;
                if (unlocks.includes('zone_cindered_reliquary')) {
                    // Old Cindered Reliquary → Ashen Crucible Tier 1
                    if (!unlocks.includes('zone_ashen_crucible_t1')) {
                        unlocks.push('zone_ashen_crucible_t1');
                    }
                    // Remove old unlock
                    const idx = unlocks.indexOf('zone_cindered_reliquary');
                    if (idx !== -1) unlocks.splice(idx, 1);
                }
                if (unlocks.includes('zone_ashen_convergence')) {
                    // Old Ashen Convergence → grant all 3 Crucible tiers
                    for (const tier of ['zone_ashen_crucible_t1', 'zone_ashen_crucible_t2', 'zone_ashen_crucible_t3']) {
                        if (!unlocks.includes(tier)) {
                            unlocks.push(tier);
                        }
                    }
                    // Remove old unlock
                    const idx = unlocks.indexOf('zone_ashen_convergence');
                    if (idx !== -1) unlocks.splice(idx, 1);
                }

                // Cleanup: remove contractor_hired if quest 60 wasn't completed
                // (was erroneously auto-granted by earlier migration)
                // Also stop any in-progress passive blueprints/milestones
                const completedQuests = fresh.player.quests.completed || [];
                if (unlocks.includes('contractor_hired') && !completedQuests.includes(60)) {
                    const idx = unlocks.indexOf('contractor_hired');
                    if (idx !== -1) unlocks.splice(idx, 1);
                    // Stop passive builds that require contractor
                    if (fresh.player.architecting) {
                        fresh.player.architecting.activeBlueprints = [];
                        fresh.player.architecting.activeMilestone = null;
                    }
                }
            }

            // Handle Daily Shop stock
            if (data.player.dailyShop) {
                fresh.player.dailyShop = data.player.dailyShop;
            }

            // Handle Corruption state (per-skill buffs)
            if (data.player.corruption) {
                fresh.player.corruption = data.player.corruption;
                // Ensure skills object exists
                if (!fresh.player.corruption.skills) {
                    fresh.player.corruption.skills = {};
                }
            }

            // Handle Pyre Veil (hidden content tracking)
            if (data.player.pyreVeil) {
                fresh.player.pyreVeil = {
                    seenLore: data.player.pyreVeil.seenLore || [],
                    loreQueue: data.player.pyreVeil.loreQueue || [],
                    discoveredMobs: data.player.pyreVeil.discoveredMobs || []
                };
            }

            // Handle Pinned Contract
            if (data.player.pinnedContract !== undefined) {
                fresh.player.pinnedContract = data.player.pinnedContract;
            }

            // Handle Glyph Pouch
            if (data.player.glyphPouch) {
                fresh.player.glyphPouch = {
                    unlocked: data.player.glyphPouch.unlocked || false,
                    slots: data.player.glyphPouch.slots || [
                        { itemId: null, quantity: 0 },
                        { itemId: null, quantity: 0 },
                        { itemId: null, quantity: 0 },
                        { itemId: null, quantity: 0 },
                        { itemId: null, quantity: 0 },
                        { itemId: null, quantity: 0 }
                    ]
                };
            }

            // Reconcile glyph pouch unlock with quest unlocks
            // Earlier builds awarded the quest unlock string but never flipped
            // glyphPouch.unlocked, leaving combat showing the pouch as locked.
            if (fresh.player.quests?.unlocks?.includes('glyph_pouch')) {
                if (!fresh.player.glyphPouch) {
                    fresh.player.glyphPouch = {
                        unlocked: true,
                        slots: [
                            { itemId: null, quantity: 0 },
                            { itemId: null, quantity: 0 },
                            { itemId: null, quantity: 0 },
                            { itemId: null, quantity: 0 },
                            { itemId: null, quantity: 0 },
                            { itemId: null, quantity: 0 }
                        ]
                    };
                } else {
                    fresh.player.glyphPouch.unlocked = true;
                }
            }

        }

        if (data.settings) {
            Object.assign(fresh.settings, data.settings);
        }

        if (data.time) {
            Object.assign(fresh.time, data.time);
        }

        this.player = fresh.player;
        this.settings = fresh.settings;
        this.time = fresh.time;

        this.recalculateTotalLevel();

        // Clean up any invalid/orphaned items from old saves
        this.cleanupInvalidItems();
    }


    export() {
        return {
            player: JSON.parse(JSON.stringify(this.player)),
            settings: JSON.parse(JSON.stringify(this.settings)),
            time: JSON.parse(JSON.stringify(this.time)),
            version: '1.10.0', // Added talent tree system
            savedAt: Date.now(),
        };
    }

    tick() {
        this.time.totalTicks++;
    }
}

export const gameState = new GameState();
export default gameState;
